import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import {
  IoWarningOutline,
  IoTrendingDown,
  IoAlert,
  IoSparklesOutline,
  IoBulbOutline,
  IoList,
  IoPeopleOutline,
  IoInformationCircleOutline,
  IoTrendingUp,
  IoHappyOutline,
  IoCheckmarkCircleOutline,
  IoLocationMarker
} from 'react-icons/io5';
import {
  HiCursorArrowRays,
  HiUserGroup,
  HiMapPin
} from 'react-icons/hi2';

// Enhanced Modal Component for Evidence
const EvidenceModal = ({ isOpen, onClose, title, evidence, recommendation }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[85vh] shadow-2xl relative overflow-hidden">
        {/* Fixed Header with Close Button */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex justify-between items-center z-10">
          <h3 className="text-lg font-bold text-gray-800 pr-8">{title}</h3>
          <button 
            onClick={onClose}
            className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 transition-all duration-200 shadow-sm hover:shadow-md"
            aria-label="Close modal"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Scrollable Content */}
        <div className="overflow-y-auto max-h-[calc(85vh-72px)]">
          <div className="p-4 space-y-4">
            <div>
              <div className="bg-gray-50 p-3 rounded-lg space-y-2">
                {evidence.map((item, idx) => (
                  <div key={idx} className="border-l-4 border-blue-400 pl-3 py-1">
                    <div className="font-medium text-gray-800 text-sm">{item.metric}</div>
                    <div className="text-gray-700 text-sm">{item.value}</div>
                    {item.context && <div className="text-gray-500 text-xs mt-1 italic">{item.context}</div>}
                  </div>
                ))}
              </div>
            </div>
            
            {recommendation && (
              <div>
                <h4 className="font-semibold text-gray-700 mb-2 flex items-center">
                  <svg className="w-4 h-4 mr-2 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  Strategic Insight
                </h4>
                <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                  <div className="text-gray-700 text-sm">{recommendation}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Simple Popover Component
const Popover = ({ children, content, position = 'top' }) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        className="cursor-help"
      >
        {children}
      </div>
      {isVisible && (
        <div className={`absolute z-10 px-3 py-2 text-xs text-white bg-gray-800 rounded-lg shadow-lg whitespace-nowrap ${
          position === 'top' ? 'bottom-full mb-2 left-1/2 transform -translate-x-1/2' :
          position === 'bottom' ? 'top-full mt-2 left-1/2 transform -translate-x-1/2' :
          position === 'left' ? 'right-full mr-2 top-1/2 transform -translate-y-1/2' :
          'left-full ml-2 top-1/2 transform -translate-y-1/2'
        }`}>
          {content}
          <div className={`absolute w-2 h-2 bg-gray-800 transform rotate-45 ${
            position === 'top' ? 'top-full left-1/2 -translate-x-1/2 -translate-y-1/2' :
            position === 'bottom' ? 'bottom-full left-1/2 -translate-x-1/2 translate-y-1/2' :
            position === 'left' ? 'left-full top-1/2 -translate-y-1/2 -translate-x-1/2' :
            'right-full top-1/2 -translate-y-1/2 translate-x-1/2'
          }`}></div>
        </div>
      )}
    </div>
  );
};

const TopPainPoints = ({ data }) => {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('detractors');
  const [modalState, setModalState] = useState({ isOpen: false, title: '', evidence: [], recommendation: '' });
  const [isCollapsed, setIsCollapsed] = useState(true); // Add collapsed state, default to true

  // Utility function to filter out unwanted subcategories
  const filterSubcategories = (subcategories) => {
    if (!subcategories || !Array.isArray(subcategories)) return [];
    return subcategories.filter(subcat => 
      subcat.subcategory && 
      subcat.subcategory.toLowerCase() !== 'staff' &&
      subcat.subcategory !== 'staff' &&
      subcat.subcategory !== 'Others'
    );
  };

  const showEvidence = (title, evidence, recommendation = '') => {
    setModalState({ isOpen: true, title, evidence, recommendation });
  };

  const closeModal = () => {
    setModalState({ isOpen: false, title: '', evidence: [], recommendation: '' });
  };

  useEffect(() => {
    if (data && data.data) {
      analyzeTopPainPoints(data);
    }
  }, [data]);

  const analyzeTopPainPoints = (feedbackData) => {
    setLoading(true);
    
    try {
      const allFeedback = feedbackData.data || [];
      
      // Filter feedback by sentiment categories
      const detractorsFeedback = allFeedback.filter(item => 
        // item.sentiment === 'Detractor' || item.rawSentiment <= 6
        item.sentiment === 'Detractor'
      );
      
      const passivesFeedback = allFeedback.filter(item => 
        // item.sentiment === 'Passive' || (item.rawSentiment >= 7 && item.rawSentiment <= 8)
        item.sentiment === 'Passive'
      );
      
      const promotersFeedback = allFeedback.filter(item => 
        // item.sentiment === 'Promoter' || item.rawSentiment >= 9
        item.sentiment === 'Promoter'
      );

      // Comprehensive analysis for each sentiment category
      const analysis = {
        detractors: {
          overallInsights: analyzeOverallPainPoints(detractorsFeedback),
          rmAnalysis: analyzeRMPerformance(detractorsFeedback, 'detractor'),
          keyFindings: generateKeyFindings(detractorsFeedback, 'detractor'),
          strategicRecommendations: generateStrategicRecommendations(detractorsFeedback, 'detractor'),
          evidenceTrails: generateEvidenceTrails(detractorsFeedback, 'detractor'),
          demographicAnalysis: generateDemographicAnalysis(detractorsFeedback, 'detractor'),
          geographicAnalysis: generateGeographicAnalysis(detractorsFeedback, 'detractor'),
          productAnalysis: analyzeProductPerformance(detractorsFeedback, 'detractor'),
          caseClassification: analyzeCaseClassification(detractorsFeedback, 'detractor'),
          interactionSubcategory: analyzeInteractionSubcategory(detractorsFeedback, 'detractor'),
          feedbackCount: detractorsFeedback.length
        },
        passives: {
          overallInsights: analyzeOverallInsights(passivesFeedback, 'passive'),
          rmAnalysis: analyzeRMPerformance(passivesFeedback, 'passive'),
          keyFindings: generateKeyFindings(passivesFeedback, 'passive'),
          strategicRecommendations: generateStrategicRecommendations(passivesFeedback, 'passive'),
          evidenceTrails: generateEvidenceTrails(passivesFeedback, 'passive'),
          demographicAnalysis: generateDemographicAnalysis(passivesFeedback, 'passive'),
          geographicAnalysis: generateGeographicAnalysis(passivesFeedback, 'passive'),
          productAnalysis: analyzeProductPerformance(passivesFeedback, 'passive'),
          caseClassification: analyzeCaseClassification(passivesFeedback, 'passive'),
          interactionSubcategory: analyzeInteractionSubcategory(passivesFeedback, 'passive'),
          feedbackCount: passivesFeedback.length
        },
        promoters: {
          overallInsights: analyzeOverallInsights(promotersFeedback, 'promoter'),
          rmAnalysis: analyzeRMPerformance(promotersFeedback, 'promoter'),
          keyFindings: generateKeyFindings(promotersFeedback, 'promoter'),
          strategicRecommendations: generateStrategicRecommendations(promotersFeedback, 'promoter'),
          evidenceTrails: generateEvidenceTrails(promotersFeedback, 'promoter'),
          demographicAnalysis: generateDemographicAnalysis(promotersFeedback, 'promoter'),
          geographicAnalysis: generateGeographicAnalysis(promotersFeedback, 'promoter'),
          productAnalysis: analyzeProductPerformance(promotersFeedback, 'promoter'),
          caseClassification: analyzeCaseClassification(promotersFeedback, 'promoter'),
          interactionSubcategory: analyzeInteractionSubcategory(promotersFeedback, 'promoter'),
          feedbackCount: promotersFeedback.length
        }
      };

      setInsights(analysis);
    } catch (error) {
      console.error('Error analyzing pain points:', error);
      setInsights(null);
    }
    
    setLoading(false);
  };

  const analyzeOverallPainPoints = (negativeFeedback) => {
    const categoryIssues = {};
    const subcategoryIssues = {};
    const productIssues = {};
    const totalIssues = negativeFeedback.length;

    negativeFeedback.forEach(item => {
      // Track product complaints
      if (item.productName) {
        productIssues[item.productName] = (productIssues[item.productName] || 0) + 1;
      }

      if (item.processed_data?.categories) {
        item.processed_data.categories.forEach(cat => {
          if (cat.sentiment === 'negative') {
            categoryIssues[cat.category] = (categoryIssues[cat.category] || 0) + 1;
          }
        });
      }

      // Collect subcategories with negative sentiment
      if (item.processed_data?.subcategories) {
        const filteredSubcategories = filterSubcategories(item.processed_data.subcategories);
        filteredSubcategories.forEach(subcat => {
          if (subcat.sentiment === 'negative') {
            const formattedSubcategory = `${subcat.category.replace(/_/g, ' ')} > ${subcat.subcategory.replace(/_/g, ' ')}`;
            subcategoryIssues[formattedSubcategory] = (subcategoryIssues[formattedSubcategory] || 0) + 1;
          }
        });
      }
    });

    const topCategory = Object.entries(categoryIssues)
      .sort((a, b) => b[1] - a[1])[0];

    const topProduct = Object.entries(productIssues)
      .sort((a, b) => b[1] - a[1])[0];

    // Get top subcategory issues
    const topSubcategoryIssues = Object.entries(subcategoryIssues)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([subcategory, count]) => ({
        subcategory: subcategory,
        count,
        percentage: Math.round((count / totalIssues) * 100),
        severity: count > totalIssues * 0.15 ? 'Critical' : count > totalIssues * 0.08 ? 'High' : 'Moderate'
      }));

    const issueDistribution = Object.entries(categoryIssues)
      .sort((a, b) => b[1] - a[1])
      .map(([category, count]) => {
        // Get subcategories for this main category
        const categorySubcategories = Object.entries(subcategoryIssues)
          .filter(([subcat]) => subcat.startsWith(category.replace(/_/g, ' ')))
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3)
          .map(([subcat, subcatCount]) => ({
            name: subcat.split(' > ')[1] || subcat,
            count: subcatCount,
            percentage: Math.round((subcatCount / count) * 100)
          }));

        return {
          category: category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          count,
          percentage: Math.round((count / totalIssues) * 100),
          severity: count > totalIssues * 0.3 ? 'Critical' : count > totalIssues * 0.15 ? 'High' : 'Moderate',
          topSubcategories: categorySubcategories
        };
      });

    return {
      totalIssues,
      primaryConcern: topCategory ? {
        category: topCategory[0].replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        count: topCategory[1],
        dominance: Math.round((topCategory[1] / totalIssues) * 100)
      } : null,
      topProduct: topProduct ? {
        name: topProduct[0],
        count: topProduct[1],
        percentage: Math.round((topProduct[1] / totalIssues) * 100)
      } : null,
      issueDistribution,
      topSubcategoryIssues
    };
  };

  // New function to handle different sentiment types
  const analyzeOverallInsights = (feedback, sentimentType = 'detractor') => {
    const categoryIssues = {};
    const subcategoryIssues = {};
    const productIssues = {};
    const totalFeedback = feedback.length;

    feedback.forEach(item => {
      // Track product mentions
      if (item.productName) {
        productIssues[item.productName] = (productIssues[item.productName] || 0) + 1;
      }

      if (item.processed_data?.categories) {
        item.processed_data.categories.forEach(cat => {
          // For promoters, focus on positive sentiment; for passives/detractors, focus on negative/neutral
          const relevantSentiment = sentimentType === 'promoter' ? 'positive' : 
                                   sentimentType === 'passive' ? ['neutral', 'negative'] : 'negative';
          
          if (Array.isArray(relevantSentiment) ? relevantSentiment.includes(cat.sentiment) : cat.sentiment === relevantSentiment) {
            categoryIssues[cat.category] = (categoryIssues[cat.category] || 0) + 1;
          }
        });
      }

      // Collect subcategories
      if (item.processed_data?.subcategories) {
        const filteredSubcategories = filterSubcategories(item.processed_data.subcategories);
        filteredSubcategories.forEach(subcat => {
          const relevantSentiment = sentimentType === 'promoter' ? 'positive' : 
                                   sentimentType === 'passive' ? ['neutral', 'negative'] : 'negative';
          
          if (Array.isArray(relevantSentiment) ? relevantSentiment.includes(subcat.sentiment) : subcat.sentiment === relevantSentiment) {
            const formattedSubcategory = `${subcat.category.replace(/_/g, ' ')} > ${subcat.subcategory.replace(/_/g, ' ')}`;
            subcategoryIssues[formattedSubcategory] = (subcategoryIssues[formattedSubcategory] || 0) + 1;
          }
        });
      }
    });

    const topCategory = Object.entries(categoryIssues)
      .sort((a, b) => b[1] - a[1])[0];

    const topProduct = Object.entries(productIssues)
      .sort((a, b) => b[1] - a[1])[0];

    // Get top subcategory insights
    const topSubcategoryIssues = Object.entries(subcategoryIssues)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([subcategory, count]) => ({
        subcategory: subcategory,
        count,
        percentage: Math.round((count / totalFeedback) * 100),
        severity: count > totalFeedback * 0.15 ? 'High' : count > totalFeedback * 0.08 ? 'Moderate' : 'Low'
      }));

    const issueDistribution = Object.entries(categoryIssues)
      .sort((a, b) => b[1] - a[1])
      .map(([category, count]) => {
        // Get subcategories for this main category
        const categorySubcategories = Object.entries(subcategoryIssues)
          .filter(([subcat]) => subcat.startsWith(category.replace(/_/g, ' ')))
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3)
          .map(([subcat, subcatCount]) => ({
            name: subcat.split(' > ')[1] || subcat,
            count: subcatCount,
            percentage: Math.round((subcatCount / count) * 100)
          }));

        return {
          category: category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          count,
          percentage: Math.round((count / totalFeedback) * 100),
          severity: count > totalFeedback * 0.3 ? 'High' : count > totalFeedback * 0.15 ? 'Moderate' : 'Low',
          topSubcategories: categorySubcategories
        };
      });

    return {
      totalIssues: totalFeedback,
      primaryConcern: topCategory ? {
        category: topCategory[0].replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        count: topCategory[1],
        dominance: Math.round((topCategory[1] / totalFeedback) * 100)
      } : null,
      topProduct: topProduct ? {
        name: topProduct[0],
        count: topProduct[1],
        percentage: Math.round((topProduct[1] / totalFeedback) * 100)
      } : null,
      issueDistribution,
      topSubcategoryIssues
    };
  };

  const analyzeProductPerformance = (feedback, sentimentType = 'detractor') => {
    const productIssues = {};
    const productComplaints = {};
    const productSubcategoryComplaints = {};
    const productRMs = {};
    const productDemographics = {};
    const productGeographic = {};

    feedback.forEach(item => {
      // Skip items with empty, null, undefined, or whitespace-only product names
      if (!item.productName || typeof item.productName !== 'string' || item.productName.trim() === '') {
        return; // Skip this item
      }
      
      const product = item.productName.trim();
      
      if (!productIssues[product]) {
        productIssues[product] = 0;
        productComplaints[product] = [];
        productSubcategoryComplaints[product] = [];
        productRMs[product] = {};
        productDemographics[product] = { ageGroups: {}, genders: {} };
        productGeographic[product] = { cities: {}, states: {} };
      }

      productIssues[product]++;

      // Track RM performance for this product
      const rm = item.rmName || 'Unassigned';
      productRMs[product][rm] = (productRMs[product][rm] || 0) + 1;

      // Track demographics for this product
      const age = parseInt(item.age) || 0;
      const ageGroup = age < 30 ? 'Under 30' : age <= 50 ? '30-50' : 'Over 50';
      const gender = item.gender === 'M' ? 'Male' : 'Female';
      
      productDemographics[product].ageGroups[ageGroup] = (productDemographics[product].ageGroups[ageGroup] || 0) + 1;
      productDemographics[product].genders[gender] = (productDemographics[product].genders[gender] || 0) + 1;

      // Track geographic data for this product
      if (item.city) productGeographic[product].cities[item.city] = (productGeographic[product].cities[item.city] || 0) + 1;
      if (item.state) productGeographic[product].states[item.state] = (productGeographic[product].states[item.state] || 0) + 1;

      // Collect complaint categories with sentiment awareness
      if (item.processed_data?.categories) {
        const relevantSentiment = sentimentType === 'promoter' ? 'positive' : 
                                 sentimentType === 'passive' ? ['neutral', 'negative'] : 'negative';
        
        const mainCategories = item.processed_data.categories
          .filter(cat => Array.isArray(relevantSentiment) ? 
            relevantSentiment.includes(cat.sentiment) : cat.sentiment === relevantSentiment)
          .map(cat => cat.category.replace(/_/g, ' '));
        productComplaints[product].push(...mainCategories);
      }

      // Collect complaint subcategories with sentiment awareness
      if (item.processed_data?.subcategories) {
        const relevantSentiment = sentimentType === 'promoter' ? 'positive' : 
                                 sentimentType === 'passive' ? ['neutral', 'negative'] : 'negative';
        
        const filteredSubcategories = filterSubcategories(item.processed_data.subcategories);
        const subcategories = filteredSubcategories
          .filter(subcat => Array.isArray(relevantSentiment) ? 
            relevantSentiment.includes(subcat.sentiment) : subcat.sentiment === relevantSentiment)
          .map(subcat => `${subcat.category.replace(/_/g, ' ')} > ${subcat.subcategory.replace(/_/g, ' ')}`);
        productSubcategoryComplaints[product].push(...subcategories);
      }
    });

    // Generate top complaints for each product
    const getTopComplaints = (complaints) => {
      const counts = {};
      complaints.forEach(complaint => {
        counts[complaint] = (counts[complaint] || 0) + 1;
      });
      return Object.entries(counts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map(([complaint, count]) => ({ complaint, count }));
    };

    // Calculate total feedback with valid product names for percentage calculation
    const validProductFeedback = feedback.filter(item => 
      item.productName && typeof item.productName === 'string' && item.productName.trim() !== ''
    );

    const topProducts = Object.entries(productIssues)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([product, count]) => {
        const topRM = Object.entries(productRMs[product]).sort((a, b) => b[1] - a[1])[0];
        const topAge = Object.entries(productDemographics[product].ageGroups).sort((a, b) => b[1] - a[1])[0];
        const topGender = Object.entries(productDemographics[product].genders).sort((a, b) => b[1] - a[1])[0];
        const topCity = Object.entries(productGeographic[product].cities).sort((a, b) => b[1] - a[1])[0];
        const topComplaints = getTopComplaints(productComplaints[product]);
        const topSubcategoryComplaints = getTopComplaints(productSubcategoryComplaints[product]);

        return {
          name: product,
          count,
          percentage: Math.round((count / validProductFeedback.length) * 100),
          mainIssue: topComplaints[0]?.complaint || 'Various issues',
          topComplaints,
          topSubcategoryComplaints,
          topRM: topRM ? { name: topRM[0], complaints: topRM[1] } : null,
          demographics: {
            topAge: topAge ? { group: topAge[0], count: topAge[1] } : null,
            topGender: topGender ? { gender: topGender[0], count: topGender[1] } : null
          },
          geographic: {
            topCity: topCity ? { city: topCity[0], count: topCity[1] } : null
          }
        };
      });

    return {
      topProducts,
      totalProductIssues: Object.keys(productIssues).length,
      mostProblematicProduct: topProducts[0] || null
    };
  };

  const analyzeRMPerformance = (feedback, sentimentType = 'detractor') => {
    const rmIssues = {};
    const rmCustomers = {};
    const rmIssueTypes = {};

    feedback.forEach(item => {
      const rm = item.rmName || 'Unassigned';
      rmIssues[rm] = (rmIssues[rm] || 0) + 1;
      
      if (!rmCustomers[rm]) {
        rmCustomers[rm] = new Set();
        rmIssueTypes[rm] = {};
      }
      rmCustomers[rm].add(item.reviewId);

      // Track issue types per RM with sentiment awareness
      if (item.processed_data?.categories) {
        item.processed_data.categories.forEach(cat => {
          const relevantSentiment = sentimentType === 'promoter' ? 'positive' : 
                                   sentimentType === 'passive' ? ['neutral', 'negative'] : 'negative';
          
          if (Array.isArray(relevantSentiment) ? relevantSentiment.includes(cat.sentiment) : cat.sentiment === relevantSentiment) {
            rmIssueTypes[rm][cat.category] = (rmIssueTypes[rm][cat.category] || 0) + 1;
          }
        });
      }
    });

    const rmPerformance = Object.entries(rmIssues)
      .map(([rm, issues]) => {
        const topIssueType = Object.entries(rmIssueTypes[rm] || {})
          .sort((a, b) => b[1] - a[1])[0];
        
        return {
          name: rm,
          dissatisfiedCustomers: rmCustomers[rm].size,
          issueCount: issues,
          topIssueType: topIssueType ? topIssueType[0].replace(/_/g, ' ') : 'Various',
          topIssueCount: topIssueType ? topIssueType[1] : 0,
          severity: issues > 5 ? 'High Risk' : issues > 2 ? 'Attention Needed' : 'Monitor'
        };
      })
      .sort((a, b) => b.issueCount - a[1]);

    const highRiskRMs = rmPerformance.filter(rm => rm.severity === 'High Risk');
    const attentionNeededRMs = rmPerformance.filter(rm => rm.severity === 'Attention Needed');

    return {
      topUnderperformingRMs: rmPerformance.slice(0, 5),
      highRiskCount: highRiskRMs.length,
      attentionNeededCount: attentionNeededRMs.length,
      summary: generateRMSummary(rmPerformance),
      aiActionPlan: generateRMActionPlan(rmPerformance, highRiskRMs, attentionNeededRMs)
    };
  };

  const generateRMSummary = (rmPerformance) => {
    const totalRMs = rmPerformance.length;
    const highRisk = rmPerformance.filter(rm => rm.severity === 'High Risk').length;
    const attentionNeeded = rmPerformance.filter(rm => rm.severity === 'Attention Needed').length;
    const percentage = Math.round((highRisk / totalRMs) * 100);

    if (totalRMs === 0) {
      return `No RM data available for analysis.`;
    } else if (highRisk === 0 && attentionNeeded === 0) {
      return `All ${totalRMs} RMs have manageable complaint levels, though individual performance monitoring remains important for service quality.`;
    } else if (percentage > 30) {
      return `Critical RM situation: ${percentage}% of relationship managers have high customer dissatisfaction rates, indicating systemic training or process issues.`;
    } else if (percentage > 15) {
      return `Moderate RM concerns: ${percentage}% of relationship managers need immediate attention to improve customer satisfaction.`;
    } else {
      return `Overall RM performance is acceptable, with ${highRisk} high-risk and ${attentionNeeded} attention-needed RMs requiring focused improvement efforts.`;
    }
  };

  const generateRMActionPlan = (rmPerformance, highRiskRMs, attentionNeededRMs) => {
    const plans = [];

    if (highRiskRMs.length > 0) {
      const commonIssues = {};
      highRiskRMs.forEach(rm => {
        commonIssues[rm.topIssueType] = (commonIssues[rm.topIssueType] || 0) + 1;
      });
      const topCommonIssue = Object.entries(commonIssues).sort((a, b) => b[1] - a[1])[0];

      plans.push({
        priority: 'Immediate',
        action: 'High-Risk RM Intervention',
        description: `Deploy immediate coaching for ${highRiskRMs.length} high-risk RMs focusing on ${topCommonIssue[0]} issues. Implement weekly 1-on-1 sessions and shadow experienced RMs.`,
        timeline: '1-2 weeks',
        impact: 'Reduce customer complaints by 40-60%'
      });
    }

    if (attentionNeededRMs.length > 0) {
      plans.push({
        priority: 'High',
        action: 'Skills Development Program',
        description: `Enroll ${attentionNeededRMs.length} RMs in targeted training modules. Focus on customer communication, issue resolution, and proactive relationship management.`,
        timeline: '3-4 weeks',
        impact: 'Improve customer satisfaction scores by 20-30%'
      });
    }

    if (rmPerformance.length > 0) {
      plans.push({
        priority: 'Medium',
        action: 'Performance Monitoring System',
        description: 'Implement automated customer feedback tracking for all RMs with monthly performance reviews and early warning alerts for declining satisfaction.',
        timeline: '4-6 weeks',
        impact: 'Prevent future issues and maintain service quality'
      });
    }

    return plans;
  };

  const generateKeyFindings = (negativeFeedback) => {
    const findings = [];
    
    // Demographic analysis
    const ageGroups = { 'Under 30': 0, '30-50': 0, 'Over 50': 0 };
    const genderDistribution = { male: 0, female: 0 };
    const locationIssues = {};
    const interactionCategoryIssues = {};

    negativeFeedback.forEach(item => {
      const age = parseInt(item.age) || 0;
      if (age < 30) ageGroups['Under 30']++;
      else if (age <= 50) ageGroups['30-50']++;
      else ageGroups['Over 50']++;

      const gender = item.gender === 'M' ? 'male' : 'female';
      genderDistribution[gender]++;

      const city = item.city || 'Unknown';
      locationIssues[city] = (locationIssues[city] || 0) + 1;

      const interactionCategory = item.interactionCategory || 'Unknown';
      interactionCategoryIssues[interactionCategory] = (interactionCategoryIssues[interactionCategory] || 0) + 1;
    });

    // Most affected age group
    const topAgeGroup = Object.entries(ageGroups).sort((a, b) => b[1] - a[1])[0];
    if (topAgeGroup) {
      findings.push({
        finding: `Customer dissatisfaction is most prevalent among ${topAgeGroup[0]} age group (${topAgeGroup[1]} complaints)`,
        insight: 'Age-specific service improvements needed',
        evidence: [
          { metric: 'Age Group Distribution', value: `Under 30: ${ageGroups['Under 30']}, 30-50: ${ageGroups['30-50']}, Over 50: ${ageGroups['Over 50']}` },
          { metric: 'Most Affected Group', value: `${topAgeGroup[0]} with ${Math.round((topAgeGroup[1]/negativeFeedback.length)*100)}% of total complaints` }
        ]
      });
    }

    // Gender analysis
    const totalGender = genderDistribution.male + genderDistribution.female;
    const genderDifference = Math.abs(genderDistribution.male - genderDistribution.female);
    if (genderDifference > totalGender * 0.15) {
      const dominantGender = genderDistribution.male > genderDistribution.female ? 'male' : 'female';
      const percentage = Math.round((Math.max(genderDistribution.male, genderDistribution.female) / totalGender) * 100);
      findings.push({
        finding: `${dominantGender.charAt(0).toUpperCase() + dominantGender.slice(1)} customers show higher dissatisfaction (${percentage}% of complaints)`,
        insight: 'Gender-specific service approach needed',
        evidence: [
          { metric: 'Gender Distribution', value: `Male: ${genderDistribution.male}, Female: ${genderDistribution.female}` },
          { metric: 'Dominant Gender Impact', value: `${percentage}% of complaints from ${dominantGender} customers` }
        ]
      });
    }

    // Interaction Category analysis
    const topInteractionCategory = Object.entries(interactionCategoryIssues).sort((a, b) => b[1] - a[1])[0];
    if (topInteractionCategory) {
      findings.push({
        finding: `${topInteractionCategory[0]} interaction category generates the highest dissatisfaction (${topInteractionCategory[1]} complaints)`,
        insight: 'Interaction category-specific process optimization required',
        evidence: [
          { metric: 'Interaction Category Distribution', value: Object.entries(interactionCategoryIssues).map(([k,v]) => `${k}: ${v}`).join(', ') },
          { metric: 'Impact', value: `${Math.round((topInteractionCategory[1]/negativeFeedback.length)*100)}% of all complaints from this interaction category` }
        ]
      });
    }

    // Geographical analysis
    const topLocation = Object.entries(locationIssues).sort((a, b) => b[1] - a[1])[0];
    if (topLocation && topLocation[1] > negativeFeedback.length * 0.1) {
      findings.push({
        finding: `${topLocation[0]} shows concentrated customer dissatisfaction (${topLocation[1]} complaints)`,
        insight: 'Regional management attention required',
        evidence: [
          { metric: 'Location Distribution', value: Object.entries(locationIssues).slice(0, 5).map(([k,v]) => `${k}: ${v}`).join(', ') },
          { metric: 'Concentration Level', value: `${Math.round((topLocation[1]/negativeFeedback.length)*100)}% of complaints from ${topLocation[0]}` }
        ]
      });
    }

    return findings;
  };

  const generateStrategicRecommendations = (negativeFeedback) => {
    const recommendations = [];
    
    // Analyze patterns for concrete recommendations with specific thresholds and logic
    const categoryIssues = {};
    const ageGroupIssues = { young: 0, middle: 0, senior: 0 };
    const interactionCategoryIssues = {};
    const locationIssues = {};

    negativeFeedback.forEach(item => {
      // Category analysis
      if (item.processed_data?.categories) {
        item.processed_data.categories.forEach(cat => {
          if (cat.sentiment === 'negative') {
            categoryIssues[cat.category] = (categoryIssues[cat.category] || 0) + 1;
          }
        });
      }

      // Age group analysis
      const age = parseInt(item.age) || 0;
      if (age < 35) ageGroupIssues.young++;
      else if (age <= 55) ageGroupIssues.middle++;
      else ageGroupIssues.senior++;

      // Interaction Category analysis
      const interactionCategory = item.interactionCategory || 'Unknown';
      interactionCategoryIssues[interactionCategory] = (interactionCategoryIssues[interactionCategory] || 0) + 1;

      // Location analysis
      const city = item.city || 'Unknown';
      locationIssues[city] = (locationIssues[city] || 0) + 1;
    });

    // LOGIC 1: Primary Issue Resolution (if any category has >20% of complaints)
    const topCategory = Object.entries(categoryIssues).sort((a, b) => b[1] - a[1])[0];
    if (topCategory) {
      const category = topCategory[0].replace(/_/g, ' ');
      const percentage = Math.round((topCategory[1] / negativeFeedback.length) * 100);
      
      // Only recommend if category represents significant portion
      if (percentage >= 20) {
        const impact = percentage > 40 ? 70 : percentage > 30 ? 60 : 50;
        recommendations.push({
          title: 'Primary Issue Resolution',
          description: `Immediate focus on ${category} improvements`,
          rationale: `${percentage}% of complaints (${topCategory[1]} cases) stem from ${category} issues - exceeds critical threshold of 20%`,
          action: `Create dedicated task force for ${category} with weekly review meetings and process optimization`,
          timeline: percentage > 30 ? '2-3 weeks' : '3-4 weeks',
          expectedImpact: `Reduce overall complaints by ${Math.round(percentage * impact / 100)}%`,
          priority: percentage > 30 ? 'Critical' : 'High',
          evidence: [
            { metric: 'Category Impact', value: `${topCategory[1]} complaints (${percentage}% of total)` },
            { metric: 'Severity Level', value: percentage > 30 ? 'Critical - Exceeds 30% threshold' : 'High - Exceeds 20% threshold' },
            { metric: 'Potential Reduction', value: `${Math.round(percentage * impact / 100)}% complaint reduction based on resolution success rate` }
          ]
        });
      }
    }

    // LOGIC 2: Demographic Strategy (if age group concentration >40%)
    const mostAffectedAge = Object.entries(ageGroupIssues).sort((a, b) => b[1] - a[1])[0];
    if (mostAffectedAge) {
      const ageGroup = mostAffectedAge[0] === 'young' ? 'younger customers (under 35)' : 
                      mostAffectedAge[0] === 'middle' ? 'middle-aged customers (35-55)' : 
                      'senior customers (over 55)';
      const agePercentage = Math.round((mostAffectedAge[1] / negativeFeedback.length) * 100);
      
      // Only recommend if age group concentration is significant
      if (agePercentage >= 40) {
        recommendations.push({
          title: 'Demographic-Targeted Strategy',
          description: `Specialized service approach for ${ageGroup}`,
          rationale: `${agePercentage}% of complaints come from this demographic (${mostAffectedAge[1]} cases) - indicates service mismatch`,
          action: `Develop age-appropriate communication channels and service delivery methods`,
          timeline: '3-6 weeks',
          expectedImpact: `Improve satisfaction scores by 25-35% for this segment`,
          priority: 'High',
          evidence: [
            { metric: 'Demographic Impact', value: `${mostAffectedAge[1]} complaints from ${ageGroup}` },
            { metric: 'Concentration', value: `${agePercentage}% of total dissatisfaction - exceeds 40% threshold` },
            { metric: 'Service Gap', value: 'Age-specific service delivery methods needed based on concentration' }
          ]
        });
      }
    }

    // LOGIC 3: Interaction Category Optimization (if category >25% of complaints)
    const topInteractionCategory = Object.entries(interactionCategoryIssues).sort((a, b) => b[1] - a[1])[0];
    if (topInteractionCategory) {
      const categoryPercentage = Math.round((topInteractionCategory[1] / negativeFeedback.length) * 100);
      
      // Only recommend if interaction category represents significant issues
      if (categoryPercentage >= 25) {
        recommendations.push({
          title: 'Interaction Category Optimization',
          description: `${topInteractionCategory[0]} experience enhancement`,
          rationale: `${categoryPercentage}% of complaints originate from ${topInteractionCategory[0]} (${topInteractionCategory[1]} cases) - exceeds 25% threshold`,
          action: `Review process efficiency and implement specialized staff training for ${topInteractionCategory[0]}`,
          timeline: '4-8 weeks',
          expectedImpact: `Reduce interaction category-specific complaints by 50-70%`,
          priority: categoryPercentage > 35 ? 'High' : 'Medium',
          evidence: [
            { metric: 'Interaction Category Impact', value: `${topInteractionCategory[1]} complaints from ${topInteractionCategory[0]}` },
            { metric: 'Category Share', value: `${categoryPercentage}% of total complaints - exceeds 25% threshold` },
            { metric: 'Process Issues', value: 'Training and efficiency gaps identified through complaint analysis' }
          ]
        });
      }
    }

    // LOGIC 4: Geographic Hotspot (if location >15% of complaints)
    const topLocation = Object.entries(locationIssues).sort((a, b) => b[1] - a[1])[0];
    if (topLocation) {
      const locationPercentage = Math.round((topLocation[1] / negativeFeedback.length) * 100);
      
      // Only recommend if geographic concentration is significant
      if (locationPercentage >= 15) {
        recommendations.push({
          title: 'Geographic Hotspot Resolution',
          description: `${topLocation[0]} regional service improvement`,
          rationale: `${locationPercentage}% of complaints from ${topLocation[0]} (${topLocation[1]} cases) - indicates regional service issues`,
          action: `Deploy regional manager for immediate assessment and local team training in ${topLocation[0]}`,
          timeline: '2-4 weeks',
          expectedImpact: `Reduce regional complaints by 60-80%`,
          priority: locationPercentage > 25 ? 'High' : 'Medium',
          evidence: [
            { metric: 'Geographic Impact', value: `${topLocation[1]} complaints from ${topLocation[0]}` },
            { metric: 'Regional Concentration', value: `${locationPercentage}% of total complaints - exceeds 15% threshold` },
            { metric: 'Regional Assessment', value: 'Local service delivery gaps identified through geographic analysis' }
          ]
        });
      }
    }

    return recommendations;
  };

  const generateEvidenceTrails = (negativeFeedback) => {
    const categoryIssues = {};
    const rmIssues = {};
    
    negativeFeedback.forEach(item => {
      if (item.processed_data?.categories) {
        item.processed_data.categories.forEach(cat => {
          if (cat.sentiment === 'negative') {
            categoryIssues[cat.category] = (categoryIssues[cat.category] || 0) + 1;
          }
        });
      }

      const rm = item.rmName || 'Unassigned';
      rmIssues[rm] = (rmIssues[rm] || 0) + 1;
    });

    return {
      primaryConcernEvidence: Object.entries(categoryIssues)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map(([category, count]) => ({
          metric: category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          value: `${count} complaints (${Math.round((count/negativeFeedback.length)*100)}%)`,
          context: `Based on ${negativeFeedback.length} total negative feedback entries`
        })),
      rmPerformanceEvidence: Object.entries(rmIssues)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([rm, count]) => ({
          metric: rm === 'Unassigned' ? 'Unassigned Customers' : rm,
          value: `${count} complaints`,
          context: `Risk level: ${count > 5 ? 'High Risk' : count > 2 ? 'Attention Needed' : 'Monitor'}`
        }))
    };
  };

  const generateDemographicAnalysis = (feedback, sentimentType = 'detractor') => {
    const ageGroups = { 'Under 30': 0, '30-50': 0, 'Over 50': 0 };
    const genderDistribution = { Male: 0, Female: 0 };
    const ageComplaints = { 'Under 30': [], '30-50': [], 'Over 50': [] };
    const genderComplaints = { Male: [], Female: [] };
    const ageSubcategoryComplaints = { 'Under 30': [], '30-50': [], 'Over 50': [] };
    const genderSubcategoryComplaints = { Male: [], Female: [] };

    feedback.forEach(item => {
      const age = parseInt(item.age) || 0;
      const ageGroup = age < 30 ? 'Under 30' : age <= 50 ? '30-50' : 'Over 50';
      const gender = item.gender === 'M' ? 'Male' : 'Female';
      
      ageGroups[ageGroup]++;
      genderDistribution[gender]++;
      
      // Collect complaint categories with sentiment awareness
      if (item.processed_data?.categories) {
        const relevantSentiment = sentimentType === 'promoter' ? 'positive' : 
                                 sentimentType === 'passive' ? ['neutral', 'negative'] : 'negative';
        
        const mainCategories = item.processed_data.categories
          .filter(cat => Array.isArray(relevantSentiment) ? 
            relevantSentiment.includes(cat.sentiment) : cat.sentiment === relevantSentiment)
          .map(cat => cat.category.replace(/_/g, ' '));
        ageComplaints[ageGroup].push(...mainCategories);
        genderComplaints[gender].push(...mainCategories);
      }

      // Collect complaint subcategories with sentiment awareness
      if (item.processed_data?.subcategories) {
        const relevantSentiment = sentimentType === 'promoter' ? 'positive' : 
                                 sentimentType === 'passive' ? ['neutral', 'negative'] : 'negative';
        
        const filteredSubcategories = filterSubcategories(item.processed_data.subcategories);
        const subcategories = filteredSubcategories
          .filter(subcat => Array.isArray(relevantSentiment) ? 
            relevantSentiment.includes(subcat.sentiment) : subcat.sentiment === relevantSentiment)
          .map(subcat => `${subcat.category.replace(/_/g, ' ')} > ${subcat.subcategory.replace(/_/g, ' ')}`);
        ageSubcategoryComplaints[ageGroup].push(...subcategories);
        genderSubcategoryComplaints[gender].push(...subcategories);
      }
    });

    // Generate top complaints for each group
    const getTopComplaints = (complaints) => {
      const counts = {};
      complaints.forEach(complaint => {
        counts[complaint] = (counts[complaint] || 0) + 1;
      });
      return Object.entries(counts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map(([complaint, count]) => ({ complaint, count }));
    };

    const ageGroupInsights = {};
    const genderInsights = {};

    Object.keys(ageGroups).forEach(group => {
      ageGroupInsights[group] = {
        count: ageGroups[group],
        topComplaints: getTopComplaints(ageComplaints[group]),
        topSubcategoryComplaints: getTopComplaints(ageSubcategoryComplaints[group]),
        mainIssue: getTopComplaints(ageComplaints[group])[0]?.complaint || 'Various issues'
      };
    });

    Object.keys(genderDistribution).forEach(gender => {
      genderInsights[gender] = {
        count: genderDistribution[gender],
        topComplaints: getTopComplaints(genderComplaints[gender]),
        topSubcategoryComplaints: getTopComplaints(genderSubcategoryComplaints[gender]),
        mainIssue: getTopComplaints(genderComplaints[gender])[0]?.complaint || 'Various issues'
      };
    });

    const totalFeedback = feedback.length;
    const mostAffectedAge = Object.entries(ageGroups).sort((a, b) => b[1] - a[1])[0];
    const mostAffectedGender = Object.entries(genderDistribution).sort((a, b) => b[1] - a[1])[0];

    return {
      ageGroups,
      genderDistribution,
      ageGroupInsights,
      genderInsights,
      totalFeedback,
      insights: {
        dominantAge: mostAffectedAge ? {
          group: mostAffectedAge[0],
          count: mostAffectedAge[1],
          percentage: Math.round((mostAffectedAge[1] / totalFeedback) * 100)
        } : null,
        dominantGender: mostAffectedGender ? {
          gender: mostAffectedGender[0],
          count: mostAffectedGender[1],
          percentage: Math.round((mostAffectedGender[1] / totalFeedback) * 100)
        } : null
      }
    };
  };

  const generateGeographicAnalysis = (feedback, sentimentType = 'detractor') => {
    const circleIssues = {};
    const circleComplaints = {};
    const circleSubcategoryComplaints = {};
    
    feedback.forEach(item => {
      const circle = item.circle || 'Unknown';
      
      circleIssues[circle] = (circleIssues[circle] || 0) + 1;

      // Collect complaint categories for each circle with sentiment awareness
      if (item.processed_data?.categories) {
        const relevantSentiment = sentimentType === 'promoter' ? 'positive' : 
                                 sentimentType === 'passive' ? ['neutral', 'negative'] : 'negative';
        
        const mainCategories = item.processed_data.categories
          .filter(cat => Array.isArray(relevantSentiment) ? 
            relevantSentiment.includes(cat.sentiment) : cat.sentiment === relevantSentiment)
          .map(cat => cat.category.replace(/_/g, ' '));
        
        if (!circleComplaints[circle]) circleComplaints[circle] = [];
        
        circleComplaints[circle].push(...mainCategories);
      }

      // Collect complaint subcategories for each circle with sentiment awareness
      if (item.processed_data?.subcategories) {
        const relevantSentiment = sentimentType === 'promoter' ? 'positive' : 
                                 sentimentType === 'passive' ? ['neutral', 'negative'] : 'negative';
        
        const filteredSubcategories = filterSubcategories(item.processed_data.subcategories);
        const subcategories = filteredSubcategories
          .filter(subcat => Array.isArray(relevantSentiment) ? 
            relevantSentiment.includes(subcat.sentiment) : subcat.sentiment === relevantSentiment)
          .map(subcat => `${subcat.category.replace(/_/g, ' ')} > ${subcat.subcategory.replace(/_/g, ' ')}`);
        
        if (!circleSubcategoryComplaints[circle]) circleSubcategoryComplaints[circle] = [];
        
        circleSubcategoryComplaints[circle].push(...subcategories);
      }
    });

    // Generate top complaints for each circle
    const getTopComplaints = (complaints) => {
      const counts = {};
      complaints.forEach(complaint => {
        counts[complaint] = (counts[complaint] || 0) + 1;
      });
      return Object.entries(counts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map(([complaint, count]) => ({ complaint, count }));
    };

    const topCircles = Object.entries(circleIssues)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([circle, count]) => ({
        name: circle,
        count,
        percentage: Math.round((count / feedback.length) * 100),
        topComplaints: getTopComplaints(circleComplaints[circle] || []),
        topSubcategoryComplaints: getTopComplaints(circleSubcategoryComplaints[circle] || []),
        mainIssue: getTopComplaints(circleComplaints[circle] || [])[0]?.complaint || 'Various issues'
      }));

    return {
      circleIssues,
      topCircles,
      totalFeedback: feedback.length
    };
  };

  const generateSpecificSuggestion = (category, severity, count) => {
    const suggestions = {
      'product_quality': `Review quality control processes and conduct immediate batch inspection. Set up customer callback program for affected ${count} customers.`,
      'delivery_issues': `Coordinate with logistics team to identify delivery bottlenecks. Implement real-time tracking updates and proactive customer communication.`,
      'customer_service': `Retrain customer service representatives on complaint resolution. Establish escalation protocols and response time targets.`,
      'billing_problems': `Audit billing system for errors and discrepancies. Provide immediate refunds/corrections for affected customers.`,
      'installation_support': `Deploy technical support team for on-site assistance. Create installation troubleshooting guides for customers.`,
      'order_management': `Review order processing workflow and identify bottlenecks. Implement order status tracking and automated updates.`,
      'communication_issues': `Establish clear communication protocols and response timelines. Train staff on effective customer communication techniques.`,
      'default': `Form a dedicated task force to address this specific issue category. Conduct root cause analysis and implement corrective measures.`
    };
    
    const categoryKey = category.toLowerCase().replace(/\s+/g, '_');
    return suggestions[categoryKey] || suggestions['default'];
  };

  const generateLongTermSolution = (category, severity) => {
    const solutions = {
      'product_quality': `Implement comprehensive quality management system with regular audits, supplier evaluations, and customer feedback integration.`,
      'delivery_issues': `Partner with reliable logistics providers, implement delivery optimization software, and establish backup delivery networks.`,
      'customer_service': `Develop customer service excellence program with regular training, performance metrics, and customer satisfaction tracking.`,
      'billing_problems': `Upgrade billing system with automated validation, real-time processing, and customer self-service portal.`,
      'installation_support': `Create comprehensive installation support network with certified technicians and remote assistance capabilities.`,
      'order_management': `Implement end-to-end order management system with real-time inventory, automated processing, and customer visibility.`,
      'communication_issues': `Establish omnichannel communication platform with integrated CRM, automated follow-ups, and customer preference management.`,
      'default': `Develop systematic approach to prevent recurring issues through process improvement, staff training, and customer feedback mechanisms.`
    };
    
    const categoryKey = category.toLowerCase().replace(/\s+/g, '_');
    return solutions[categoryKey] || solutions['default'];
  };

  const analyzePainPointInsights = (feedback, category, sentimentType = 'detractor') => {
    // Filter feedback for this specific category with sentiment awareness
    const categoryFeedback = feedback.filter(item => 
      item.processed_data?.categories?.some(cat => {
        const relevantSentiment = sentimentType === 'promoter' ? 'positive' : 
                                 sentimentType === 'passive' ? ['neutral', 'negative'] : 'negative';
        
        const sentimentMatch = Array.isArray(relevantSentiment) ? 
          relevantSentiment.includes(cat.sentiment) : cat.sentiment === relevantSentiment;
        
        return sentimentMatch && cat.category.replace(/_/g, ' ').toLowerCase().includes(category.toLowerCase());
      })
    );

    // Product analysis
    const productCounts = {};
    categoryFeedback.forEach(item => {
      if (item.productName) {
        productCounts[item.productName] = (productCounts[item.productName] || 0) + 1;
      }
    });
    const topProduct = Object.entries(productCounts).sort((a, b) => b[1] - a[1])[0];

    // Demographics analysis
    const ageCounts = {};
    const genderCounts = {};
    categoryFeedback.forEach(item => {
      const age = parseInt(item.age) || 0;
      const ageGroup = age < 30 ? 'Under 30' : age <= 50 ? '30-50' : 'Over 50';
      const gender = item.gender === 'M' ? 'Male' : 'Female';
      
      ageCounts[ageGroup] = (ageCounts[ageGroup] || 0) + 1;
      genderCounts[gender] = (genderCounts[gender] || 0) + 1;
    });
    const topAge = Object.entries(ageCounts).sort((a, b) => b[1] - a[1])[0];
    const topGender = Object.entries(genderCounts).sort((a, b) => b[1] - a[1])[0];

    // Geographic analysis
    const circleCounts = {};
    categoryFeedback.forEach(item => {
      if (item.circle) {
        circleCounts[item.circle] = (circleCounts[item.circle] || 0) + 1;
      }
    });
    const topCircle = Object.entries(circleCounts).sort((a, b) => b[1] - a[1])[0];

    // Interaction subcategory analysis
    const interactionCounts = {};
    categoryFeedback.forEach(item => {
      if (item.interactionSubCategory) {
        interactionCounts[item.interactionSubCategory] = (interactionCounts[item.interactionSubCategory] || 0) + 1;
      }
    });
    const topInteraction = Object.entries(interactionCounts).sort((a, b) => b[1] - a[1])[0];

    return {
      totalCount: categoryFeedback.length,
      topProduct: topProduct ? { name: topProduct[0], count: topProduct[1] } : null,
      demographics: {
        topAge: topAge ? { group: topAge[0], count: topAge[1] } : null,
        topGender: topGender ? { gender: topGender[0], count: topGender[1] } : null
      },
      geographic: {
        topCircle: topCircle ? { circle: topCircle[0], count: topCircle[1] } : null
      },
      interaction: {
        topSubcategory: topInteraction ? { subcategory: topInteraction[0], count: topInteraction[1] } : null
      }
    };
  };

  const analyzeCaseClassification = (negativeFeedback) => {
    const classificationIssues = {};
    const classificationComplaints = {};
    const classificationSubcategoryComplaints = {};

    negativeFeedback.forEach(item => {
      const classification = item.caseClassification || 'Unknown';
      
      classificationIssues[classification] = (classificationIssues[classification] || 0) + 1;

      // Collect complaint categories for each classification
      if (item.processed_data?.categories) {
        const mainCategories = item.processed_data.categories
          .filter(cat => cat.sentiment === 'negative')
          .map(cat => cat.category.replace(/_/g, ' '));
        
        if (!classificationComplaints[classification]) classificationComplaints[classification] = [];
        
        classificationComplaints[classification].push(...mainCategories);
      }

      // Collect complaint subcategories for each classification
      if (item.processed_data?.subcategories) {
        const filteredSubcategories = filterSubcategories(item.processed_data.subcategories);
        const subcategories = filteredSubcategories
          .filter(subcat => subcat.sentiment === 'negative')
          .map(subcat => `${subcat.category.replace(/_/g, ' ')} > ${subcat.subcategory.replace(/_/g, ' ')}`);
        
        if (!classificationSubcategoryComplaints[classification]) classificationSubcategoryComplaints[classification] = [];
        
        classificationSubcategoryComplaints[classification].push(...subcategories);
      }
    });

    // Generate top complaints for each classification
    const getTopComplaints = (complaints) => {
      const counts = {};
      complaints.forEach(complaint => {
        counts[complaint] = (counts[complaint] || 0) + 1;
      });
      return Object.entries(counts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map(([complaint, count]) => ({ complaint, count }));
    };

    const topClassifications = Object.entries(classificationIssues)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([classification, count]) => ({
        name: classification,
        count,
        percentage: Math.round((count / negativeFeedback.length) * 100),
        topComplaints: getTopComplaints(classificationComplaints[classification] || []),
        topSubcategoryComplaints: getTopComplaints(classificationSubcategoryComplaints[classification] || []),
        mainIssue: getTopComplaints(classificationComplaints[classification] || [])[0]?.complaint || 'Various issues'
      }));

    return {
      classificationIssues,
      topClassifications,
      totalFeedback: negativeFeedback.length,
      mostProblematicClassification: topClassifications[0] || null
    };
  };

  const analyzeInteractionSubcategory = (negativeFeedback) => {
    const subcategoryIssues = {};
    const subcategoryComplaints = {};
    const subcategorySubcategoryComplaints = {};

    negativeFeedback.forEach(item => {
      const subcategory = item.interactionSubCategory || 'Unknown';
      
      subcategoryIssues[subcategory] = (subcategoryIssues[subcategory] || 0) + 1;

      // Collect complaint categories for each interaction subcategory
      if (item.processed_data?.categories) {
        const mainCategories = item.processed_data.categories
          .filter(cat => cat.sentiment === 'negative')
          .map(cat => cat.category.replace(/_/g, ' '));
        
        if (!subcategoryComplaints[subcategory]) subcategoryComplaints[subcategory] = [];
        
        subcategoryComplaints[subcategory].push(...mainCategories);
      }

      // Collect complaint subcategories for each interaction subcategory
      if (item.processed_data?.subcategories) {
        const filteredSubcategories = filterSubcategories(item.processed_data.subcategories);
        const subcategories = filteredSubcategories
          .filter(subcat => subcat.sentiment === 'negative')
          .map(subcat => `${subcat.category.replace(/_/g, ' ')} > ${subcat.subcategory.replace(/_/g, ' ')}`);
        
        if (!subcategorySubcategoryComplaints[subcategory]) subcategorySubcategoryComplaints[subcategory] = [];
        
        subcategorySubcategoryComplaints[subcategory].push(...subcategories);
      }
    });

    // Generate top complaints for each interaction subcategory
    const getTopComplaints = (complaints) => {
      const counts = {};
      complaints.forEach(complaint => {
        counts[complaint] = (counts[complaint] || 0) + 1;
      });
      return Object.entries(counts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map(([complaint, count]) => ({ complaint, count }));
    };

    const topInteractionSubcategories = Object.entries(subcategoryIssues)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([subcategory, count]) => ({
        name: subcategory,
        count,
        percentage: Math.round((count / negativeFeedback.length) * 100),
        topComplaints: getTopComplaints(subcategoryComplaints[subcategory] || []),
        topSubcategoryComplaints: getTopComplaints(subcategorySubcategoryComplaints[subcategory] || []),
        mainIssue: getTopComplaints(subcategoryComplaints[subcategory] || [])[0]?.complaint || 'Various issues'
      }));

    return {
      subcategoryIssues,
      topInteractionSubcategories,
      totalFeedback: negativeFeedback.length,
      mostProblematicSubcategory: topInteractionSubcategories[0] || null
    };
  };

  if (loading) {
    return (
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/3"></div>
          <div className="grid grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!insights) {
    return (
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div className="text-center py-8 text-gray-500">
          Unable to analyze customer insights. Please check the data.
        </div>
      </div>
    );
  }

  // Get current tab data
  const currentInsights = insights[activeTab];
  
  // Add null checking for currentInsights
  if (!currentInsights || !currentInsights.overallInsights) {
    return (
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div className="text-center py-8 text-gray-500">
          Unable to analyze customer insights for {activeTab}. Please check the data.
        </div>
      </div>
    );
  }
  
  const sentimentConfig = {
    detractors: {
      title: 'Customer Pain Points & Insights',
      subtitle: 'AI-powered analysis to identify improvement opportunities',
      icon: IoWarningOutline,
      color: 'red',
      description: 'customer concerns with actionable recommendations'
    },
    passives: {
      title: 'Passive Customer Analysis',
      subtitle: 'AI-powered analysis to convert passives to promoters',
      icon: IoTrendingUp,
      color: 'orange',
      description: 'passive feedback with conversion strategies'
    },
    promoters: {
      title: 'Promoter Success Analysis',
      subtitle: 'AI-powered analysis of what drives customer satisfaction',
      icon: IoHappyOutline,
      color: 'green',
      description: 'positive experiences with replication strategies'
    }
  };

  const config = sentimentConfig[activeTab];

  return (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
      <EvidenceModal 
        isOpen={modalState.isOpen}
        onClose={closeModal}
        title={modalState.title}
        evidence={modalState.evidence}
        recommendation={modalState.recommendation}
      />

      {/* Header with Tabs */}
      <div className="mb-6">
        {/* Main Header with Toggle - Always Visible */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <IoWarningOutline className="w-8 h-8 text-gray-600 mr-3" />
            <div>
              <h3 className="text-xl font-semibold text-gray-800">Customer Insights Analysis</h3>
              <p className="text-sm text-gray-600">
                Comprehensive analysis across all customer segments
              </p>
            </div>
          </div>
          
          {/* Collapse/Expand Toggle */}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors"
          >
            <span>{isCollapsed ? 'Expand' : 'Collapse'}</span>
            <svg 
              className={`w-4 h-4 transform transition-transform ${isCollapsed ? 'rotate-0' : 'rotate-180'}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Everything below is collapsible */}
      {!isCollapsed && (
        <>
          {/* Tab Navigation */}
          <div className="border-b border-gray-200 mb-4">
            <nav className="-mb-px flex space-x-8">
              {[
                { key: 'detractors', label: 'Detractors', icon: IoWarningOutline, color: 'red', count: insights.detractors.feedbackCount },
                { key: 'passives', label: 'Passives', icon: IoTrendingUp, color: 'orange', count: insights.passives.feedbackCount },
                { key: 'promoters', label: 'Promoters', icon: IoHappyOutline, color: 'green', count: insights.promoters.feedbackCount }
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`group inline-flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.key
                      ? `${tab.color === 'red' ? 'border-red-500 text-red-600' : 
                           tab.color === 'orange' ? 'border-orange-500 text-orange-600' : 
                           'border-green-500 text-green-600'}`
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className={`mr-2 h-4 w-4 ${
                    activeTab === tab.key 
                      ? `${tab.color === 'red' ? 'text-red-500' : 
                           tab.color === 'orange' ? 'text-orange-500' : 
                           'text-green-500'}` 
                      : 'text-gray-400 group-hover:text-gray-500'
                  }`} />
                  {tab.label}
                  <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                    activeTab === tab.key 
                      ? `${tab.color === 'red' ? 'bg-red-100 text-red-700' : 
                           tab.color === 'orange' ? 'bg-orange-100 text-orange-700' : 
                           'bg-green-100 text-green-700'}` 
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {tab.count}
                  </span>
                </button>
              ))}
            </nav>
          </div>

          {/* Active Tab Header */}
          <div className="flex items-center mb-6">
            <config.icon className={`w-6 h-6 text-${config.color}-500 mr-3`} />
            <div>
              <h4 className="text-lg font-semibold text-gray-800">{config.title}</h4>
              <p className="text-sm text-gray-600">
                {config.subtitle} - {currentInsights.feedbackCount} {config.description}
              </p>
            </div>
          </div>

          {/* All Content Sections - Also Collapsible */}
          <div className="space-y-3 mb-6">
            {/* Top Row - Critical Insights */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {/* Primary Concern */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className={`bg-gradient-to-r p-3 rounded-lg border ${
                  config.color === 'red' ? 'from-red-50 to-orange-50 border-red-200' :
                  config.color === 'orange' ? 'from-orange-50 to-yellow-50 border-orange-200' :
                  'from-green-50 to-blue-50 border-green-200'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center">
                    <IoTrendingDown className={`w-4 h-4 mr-2 ${
                      config.color === 'red' ? 'text-red-600' :
                      config.color === 'orange' ? 'text-orange-600' :
                      'text-green-600'
                    }`} />
                    <h4 className={`font-semibold text-sm ${
                      config.color === 'red' ? 'text-red-800' :
                      config.color === 'orange' ? 'text-orange-800' :
                      'text-green-800'
                    }`}>
                      {activeTab === 'promoters' ? 'Top Success Factor' : activeTab === 'passives' ? 'Key Opportunity' : 'Primary Concern'}
                    </h4>
                  </div>
                  <button
                    onClick={() => showEvidence(
                      `${activeTab === 'promoters' ? 'Top Success Factor' : activeTab === 'passives' ? 'Key Opportunity' : 'Primary Concern'} Analysis`,
                      currentInsights.evidenceTrails.primaryConcernEvidence,
                      activeTab === 'promoters' 
                        ? 'This category represents the highest source of customer satisfaction and should be replicated across all touchpoints.'
                        : activeTab === 'passives'
                        ? 'This category represents the highest opportunity for converting passive customers to promoters through targeted improvements.'
                        : 'This category represents the highest concentration of customer dissatisfaction and should receive immediate management attention and resource allocation.'
                    )}
                    className={`p-1 rounded hover:${
                      config.color === 'red' ? 'bg-red-200' :
                      config.color === 'orange' ? 'bg-orange-200' :
                      'bg-green-200'
                    }`}
                  >
                    <HiCursorArrowRays className={`w-3 h-3 cursor-pointer ${
                      config.color === 'red' ? 'text-red-600' :
                      config.color === 'orange' ? 'text-orange-600' :
                      'text-green-600'
                    }`} />
                  </button>
                </div>
                {currentInsights.overallInsights.primaryConcern && (
                  <div className="space-y-2">
                    <div>
                      <p className={`text-sm font-bold leading-tight ${
                        config.color === 'red' ? 'text-red-800' :
                        config.color === 'orange' ? 'text-orange-800' :
                        'text-green-800'
                      }`}>
                        {currentInsights.overallInsights.primaryConcern.category}
                      </p>
                      <p className={`text-xs ${
                        config.color === 'red' ? 'text-red-700' :
                        config.color === 'orange' ? 'text-orange-700' :
                        'text-green-700'
                      }`}>
                        {currentInsights.overallInsights.primaryConcern.dominance}% • {currentInsights.overallInsights.primaryConcern.count} {activeTab === 'promoters' ? 'positive mentions' : 'feedback items'}
                      </p>
                    </div>
                    
                    {/* Top Subcategories within Primary Concern */}
                    {currentInsights.overallInsights.topSubcategoryIssues && currentInsights.overallInsights.topSubcategoryIssues.length > 0 && (
                      <div className={`pt-1 border-t ${
                        config.color === 'red' ? 'border-red-200' :
                        config.color === 'orange' ? 'border-orange-200' :
                        'border-green-200'
                      }`}>
                        <p className={`text-xs font-medium mb-1 ${
                          config.color === 'red' ? 'text-red-700' :
                          config.color === 'orange' ? 'text-orange-700' :
                          'text-green-700'
                        }`}>Top Areas:</p>
                        <div className="space-y-1">
                          {currentInsights.overallInsights.topSubcategoryIssues
                            .filter(subcat => subcat.subcategory.toLowerCase().includes(currentInsights.overallInsights.primaryConcern.category.toLowerCase()))
                            .slice(0, 2)
                            .map((subcat, idx) => (
                              <div key={idx} className={`text-xs ${
                                config.color === 'red' ? 'text-red-600' :
                                config.color === 'orange' ? 'text-orange-600' :
                                'text-green-600'
                              }`}>
                                <span className="font-medium">{subcat.subcategory.split(' > ')[1] || subcat.subcategory}:</span>
                                <span className="ml-1">{subcat.percentage}% ({subcat.count})</span>
                              </div>
                            ))}
                          {currentInsights.overallInsights.topSubcategoryIssues
                            .filter(subcat => subcat.subcategory.toLowerCase().includes(currentInsights.overallInsights.primaryConcern.category.toLowerCase()))
                            .length === 0 && (
                            <div className={`text-xs ${
                              config.color === 'red' ? 'text-red-600' :
                              config.color === 'orange' ? 'text-orange-600' :
                              'text-green-600'
                            }`}>
                              {currentInsights.overallInsights.topSubcategoryIssues.slice(0, 2).map((subcat, idx) => (
                                <div key={idx}>
                                  <span className="font-medium">{subcat.subcategory.split(' > ')[1] || subcat.subcategory}:</span>
                                  <span className="ml-1">{subcat.percentage}% ({subcat.count})</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </motion.div>

              {/* Product Issues */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
                className={`p-3 rounded-lg border ${
                  activeTab === 'promoters' 
                    ? 'bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200' 
                    : 'bg-gradient-to-r from-orange-50 to-yellow-50 border-orange-200'
                }`}
              >
                <div className="flex items-center mb-2">
                  <IoAlert className={`w-4 h-4 mr-2 ${
                    activeTab === 'promoters' ? 'text-blue-600' : 'text-orange-600'
                  }`} />
                  <h4 className={`font-semibold text-sm ${
                    activeTab === 'promoters' ? 'text-blue-800' : 'text-orange-800'
                  }`}>
                    {activeTab === 'promoters' ? 'Top Product Success' : 'Top Product Focus'}
                  </h4>
                </div>
                <div className="space-y-1">
                  {currentInsights.overallInsights.topProduct ? (
                    <>
                      <p className={`text-sm font-bold leading-tight ${
                        activeTab === 'promoters' ? 'text-blue-800' : 'text-orange-800'
                      }`}>
                        {currentInsights.overallInsights.topProduct.name}
                      </p>
                      <p className={`text-xs ${
                        activeTab === 'promoters' ? 'text-blue-700' : 'text-orange-700'
                      }`}>
                        {currentInsights.overallInsights.topProduct.percentage}% • {currentInsights.overallInsights.topProduct.count} {activeTab === 'promoters' ? 'positive mentions' : 'feedback items'}
                      </p>
                    </>
                  ) : (
                    <p className={`text-sm ${
                      activeTab === 'promoters' ? 'text-blue-700' : 'text-orange-700'
                    }`}>No product data available</p>
                  )}
                </div>
              </motion.div>

              {/* Demographics Insight */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.2 }}
                className="bg-gradient-to-r from-blue-50 to-purple-50 p-3 rounded-lg border border-blue-200"
              >
                <div className="flex items-center mb-2">
                  <HiUserGroup className="w-4 h-4 mr-2 text-blue-600" />
                  <h4 className="font-semibold text-blue-800 text-sm">Demographics</h4>
                </div>
                <div className="space-y-1">
                  {currentInsights.demographicAnalysis.insights.dominantAge && (
                    <>
                      <p className="text-sm text-blue-800 font-bold leading-tight">
                        {currentInsights.demographicAnalysis.insights.dominantAge.group} {activeTab === 'promoters' ? 'most engaged' : 'most affected'}
                      </p>
                      <p className="text-xs text-blue-700 truncate">
                        {currentInsights.demographicAnalysis.ageGroupInsights[currentInsights.demographicAnalysis.insights.dominantAge.group]?.mainIssue}
                      </p>
                    </>
                  ) || (
                    <p className="text-sm text-blue-700">No demographic data available</p>
                  )}
                </div>
              </motion.div>
            </div>

            {/* Bottom Row - Secondary Insights */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {/* Geographic Hotspot */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.3 }}
                className="bg-gradient-to-r from-purple-50 to-pink-50 p-3 rounded-lg border border-purple-200"
              >
                <div className="flex items-center mb-2">
                  <HiMapPin className="w-4 h-4 mr-2 text-purple-600" />
                  <h4 className="font-semibold text-purple-800 text-sm">Geographic</h4>
                </div>
                <div className="space-y-1">
                  {currentInsights.geographicAnalysis.topCircles.length > 0 && (
                    <>
                      <p className="text-sm text-purple-800 font-bold leading-tight">
                        {currentInsights.geographicAnalysis.topCircles[0].name}
                      </p>
                      <p className="text-xs text-purple-700 truncate">
                        {currentInsights.geographicAnalysis.topCircles[0].percentage}% • {currentInsights.geographicAnalysis.topCircles[0].mainIssue}
                      </p>
                    </>
                  ) || (
                    <p className="text-sm text-purple-700">No geographic data available</p>
                  )}
                </div>
              </motion.div>

              {/* Interaction Subcategory Insight */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.4 }}
                className="bg-gradient-to-r from-teal-50 to-cyan-50 p-3 rounded-lg border border-teal-200"
              >
                <div className="flex items-center mb-2">
                  <IoPeopleOutline className="w-4 h-4 mr-2 text-teal-600" />
                  <h4 className="font-semibold text-teal-800 text-sm">Interaction Type</h4>
                </div>
                <div className="space-y-1">
                  {currentInsights.interactionSubcategory.mostProblematicSubcategory ? (
                    <>
                      <p className="text-sm text-teal-800 font-bold leading-tight">
                        {currentInsights.interactionSubcategory.mostProblematicSubcategory.name}
                      </p>
                      <p className="text-xs text-teal-700">
                        {currentInsights.interactionSubcategory.mostProblematicSubcategory.percentage}% • {currentInsights.interactionSubcategory.mostProblematicSubcategory.count} {activeTab === 'promoters' ? 'positive interactions' : 'feedback items'}
                      </p>
                    </>
                  ) : (
                    <p className="text-sm text-teal-700">No interaction data available</p>
                  )}
                </div>
              </motion.div>

              {/* Case Classification Insight */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.5 }}
                className="bg-gradient-to-r from-indigo-50 to-purple-50 p-3 rounded-lg border border-indigo-200"
              >
                <div className="flex items-center mb-2">
                  <IoInformationCircleOutline className="w-4 h-4 mr-2 text-indigo-600" />
                  <h4 className="font-semibold text-indigo-800 text-sm">Case Type</h4>
                </div>
                <div className="space-y-1">
                  {currentInsights.caseClassification.mostProblematicClassification ? (
                    <>
                      <p className="text-sm text-indigo-800 font-bold leading-tight">
                        {currentInsights.caseClassification.mostProblematicClassification.name}
                      </p>
                      <p className="text-xs text-indigo-700">
                        {currentInsights.caseClassification.mostProblematicClassification.percentage}% • {currentInsights.caseClassification.mostProblematicClassification.count} {activeTab === 'promoters' ? 'positive cases' : 'feedback items'}
                      </p>
                    </>
                  ) : (
                    <p className="text-sm text-indigo-700">No case data available</p>
                  )}
                </div>
              </motion.div>
            </div>
          </div>

          {/* AI-Generated Analysis Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.4 }}
            className={`p-4 rounded-lg border mb-4 ${
              config.color === 'red' ? 'bg-gradient-to-r from-red-50 to-orange-50 border-red-200' :
              config.color === 'orange' ? 'bg-gradient-to-r from-orange-50 to-yellow-50 border-orange-200' :
              'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200'
            }`}
          >
            <div className="flex items-center mb-3">
              <IoSparklesOutline className={`w-4 h-4 mr-2 ${
                config.color === 'red' ? 'text-red-600' :
                config.color === 'orange' ? 'text-orange-600' :
                'text-green-600'
              }`} />
              <h4 className={`font-medium text-sm ${
                config.color === 'red' ? 'text-red-800' :
                config.color === 'orange' ? 'text-orange-800' :
                'text-green-800'
              }`}>
                {activeTab === 'promoters' ? 'Success Factors' : activeTab === 'passives' ? 'Opportunity Areas' : 'Pain Points'}
              </h4>
              <span className={`ml-2 px-2 py-1 text-xs rounded-full font-medium ${
                config.color === 'red' ? 'bg-red-100 text-red-700' :
                config.color === 'orange' ? 'bg-orange-100 text-orange-700' :
                'bg-green-100 text-green-700'
              }`}>
                🤖 AI Powered
              </span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {currentInsights?.overallInsights?.issueDistribution?.slice(0, 6).map((issue, idx) => {
                const painPointInsights = analyzePainPointInsights(
                  data.data.filter(item => {
                    // if (activeTab === 'detractors') return item.sentiment === 'Detractor' || item.rawSentiment <= 6;
                    if (activeTab === 'detractors') return item.sentiment === 'Detractor';
                    // if (activeTab === 'passives') return item.sentiment === 'Passive' || (item.rawSentiment >= 7 && item.rawSentiment <= 8);
                    if (activeTab === 'passives') return item.sentiment === 'Passive';
                    // if (activeTab === 'promoters') return item.sentiment === 'Promoter' || item.rawSentiment >= 9;
                    if (activeTab === 'promoters') return item.sentiment === 'Promoter';
                    return false;
                  }), 
                  issue.category,
                  activeTab === 'promoters' ? 'promoter' : activeTab === 'passives' ? 'passive' : 'detractor'
                );
                
                return (
                  <div
                    key={idx}
                    className="bg-white p-3 rounded-lg border border-gray-200 transition-all hover:shadow-md hover:border-blue-300"
                  >
                    {/* Key Insight at Top */}
                    <div className={`mb-3 p-2 rounded-lg ${
                      config.color === 'red' ? 'bg-gradient-to-r from-red-100 to-orange-100' :
                      config.color === 'orange' ? 'bg-gradient-to-r from-orange-100 to-yellow-100' :
                      'bg-gradient-to-r from-green-100 to-emerald-100'
                    }`}>
                      <div className="flex items-center mb-1">
                        <IoBulbOutline className="w-3 h-3 text-gray-800 mr-1" />
                        <h5 className="font-medium text-gray-800 text-xs">Key Insight</h5>
                      </div>
                      <p className="text-xs text-gray-700">
                        <strong>{issue.category}</strong> {activeTab === 'promoters' ? 'drives satisfaction for' : 'affects'} {painPointInsights.totalCount} customers ({issue.percentage}% of {activeTab === 'promoters' ? 'positive feedback' : 'complaints'}).
                        {issue.topSubcategories && issue.topSubcategories.length > 0 && (
                          <>
                            {' '}The {activeTab === 'promoters' ? 'top success factors are' : 'primary issues are'}: <strong>{issue.topSubcategories[0].name}</strong> ({issue.topSubcategories[0].count} {activeTab === 'promoters' ? 'positive mentions' : 'complaints'}, {issue.topSubcategories[0].percentage}% of category)
                            {issue.topSubcategories[1] && (
                              <>, <strong>{issue.topSubcategories[1].name}</strong> ({issue.topSubcategories[1].count} {activeTab === 'promoters' ? 'positive mentions' : 'complaints'}, {issue.topSubcategories[1].percentage}% of category)</>
                            )}
                            {issue.topSubcategories[2] && (
                              <>, and <strong>{issue.topSubcategories[2].name}</strong> ({issue.topSubcategories[2].count} {activeTab === 'promoters' ? 'positive mentions' : 'complaints'}, {issue.topSubcategories[2].percentage}% of category)</>
                            )}.
                          </>
                        )}
                        {painPointInsights.topProduct && ` Top ${activeTab === 'promoters' ? 'performing' : 'affected'} product: ${painPointInsights.topProduct.name}.`}
                      </p>
                    </div>

                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="flex items-center mb-1">
                          <h5 className="font-medium text-gray-800 text-xs mr-2">{issue.category}</h5>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            activeTab === 'promoters'
                              ? (issue.severity === 'Critical' ? 'bg-green-100 text-green-700' :
                                 issue.severity === 'High' ? 'bg-emerald-100 text-emerald-700' :
                                 'bg-teal-100 text-teal-700')
                              : (issue.severity === 'Critical' ? 'bg-red-100 text-red-700' :
                                 issue.severity === 'High' ? 'bg-orange-100 text-orange-700' :
                                 'bg-blue-100 text-blue-700')
                          }`}>
                            {activeTab === 'promoters'
                              ? (issue.severity === 'Critical' ? 'Excellent' :
                                 issue.severity === 'High' ? 'Strong' : 'Good')
                              : issue.severity
                            }
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 mb-2">{issue.count} {activeTab === 'promoters' ? 'positive mentions' : 'complaints'} ({issue.percentage}%)</div>
                      </div>
                    </div>

                    {/* Subcategories Highlight */}
                    {issue.topSubcategories && issue.topSubcategories.length > 0 && (
                      <div className={`mb-3 p-2 rounded border ${
                        activeTab === 'promoters' 
                          ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200'
                          : 'bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-200'
                      }`}>
                        <div className="flex items-center mb-2">
                          <IoList className={`w-3 h-3 mr-1 ${
                            activeTab === 'promoters' ? 'text-green-600' : 'text-orange-600'
                          }`} />
                          <span className={`font-medium text-xs ${
                            activeTab === 'promoters' ? 'text-green-800' : 'text-orange-800'
                          }`}>
                            {activeTab === 'promoters' ? 'Top Success Areas:' : 'Top Problem Areas:'}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 gap-1">
                          {issue.topSubcategories.slice(0, 3).map((subcat, subIdx) => (
                            <div key={subIdx} className={`flex items-center justify-between bg-white p-1.5 rounded text-xs border ${
                              activeTab === 'promoters' ? 'border-green-300' : 'border-yellow-300'
                            }`}>
                              <div className="flex items-center flex-1">
                                <span className={`w-2 h-2 rounded-full mr-2 ${
                                  activeTab === 'promoters' 
                                    ? (subIdx === 0 ? 'bg-green-500' : subIdx === 1 ? 'bg-emerald-500' : 'bg-teal-500')
                                    : (subIdx === 0 ? 'bg-red-500' : subIdx === 1 ? 'bg-orange-500' : 'bg-yellow-500')
                                }`}></span>
                                <span className="text-gray-700 font-medium truncate">{subcat.name}</span>
                              </div>
                              <div className="flex items-center space-x-2 ml-2">
                                <span className={`font-medium ${
                                  activeTab === 'promoters' ? 'text-green-700' : 'text-orange-700'
                                }`}>{subcat.count}</span>
                                <span className={`text-xs ${
                                  activeTab === 'promoters' ? 'text-green-600' : 'text-orange-600'
                                }`}>({subcat.percentage}%)</span>
                                <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                                  activeTab === 'promoters'
                                    ? (subcat.percentage > 40 ? 'bg-green-100 text-green-700' :
                                       subcat.percentage > 25 ? 'bg-emerald-100 text-emerald-700' :
                                       'bg-teal-100 text-teal-700')
                                    : (subcat.percentage > 40 ? 'bg-red-100 text-red-700' :
                                       subcat.percentage > 25 ? 'bg-orange-100 text-orange-700' :
                                       'bg-yellow-100 text-yellow-700')
                                }`}>
                                  {activeTab === 'promoters'
                                    ? (subcat.percentage > 40 ? 'Excellent' :
                                       subcat.percentage > 25 ? 'Strong' : 'Good')
                                    : (subcat.percentage > 40 ? 'Critical' :
                                       subcat.percentage > 25 ? 'High' : 'Moderate')
                                  }
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Comprehensive Insights */}
                    <div className="space-y-2 mb-3">
                      {/* Product Insight */}
                      {painPointInsights.topProduct && (
                        <div className={`p-2 rounded text-xs ${
                          activeTab === 'promoters' 
                            ? 'bg-gradient-to-r from-green-50 to-emerald-50' 
                            : 'bg-gradient-to-r from-orange-50 to-yellow-50'
                        }`}>
                          <span className={`font-medium ${
                            activeTab === 'promoters' ? 'text-green-800' : 'text-orange-800'
                          }`}>📦 Product {activeTab === 'promoters' ? 'Success:' : 'Impact:'}</span>
                          <span className={`ml-1 ${
                            activeTab === 'promoters' ? 'text-green-700' : 'text-orange-700'
                          }`}>
                            {painPointInsights.topProduct.name} accounts for {painPointInsights.topProduct.count} {activeTab === 'promoters' ? 'positive mentions' : 'complaints'} 
                            ({Math.round((painPointInsights.topProduct.count / painPointInsights.totalCount) * 100)}%)
                          </span>
                        </div>
                      )}

                      {/* Demographics Insight */}
                      {painPointInsights.demographics.topAge && (
                        <div className={`p-2 rounded text-xs ${
                          config.color === 'red' ? 'bg-gradient-to-r from-red-50 to-orange-50' :
                          config.color === 'orange' ? 'bg-gradient-to-r from-orange-50 to-yellow-50' :
                          'bg-gradient-to-r from-green-50 to-emerald-50'
                        }`}>
                          <span className={`font-medium ${
                            config.color === 'red' ? 'text-red-800' :
                            config.color === 'orange' ? 'text-orange-800' :
                            'text-green-800'
                          }`}>👥 Demographics:</span>
                          <span className={`ml-1 ${
                            config.color === 'red' ? 'text-red-700' :
                            config.color === 'orange' ? 'text-orange-700' :
                            'text-green-700'
                          }`}>
                            {painPointInsights.demographics.topAge.group} age group ({painPointInsights.demographics.topAge.count} cases)
                            {painPointInsights.demographics.topGender && `, ${painPointInsights.demographics.topGender.gender} customers (${painPointInsights.demographics.topGender.count})`}
                          </span>
                        </div>
                      )}

                      {/* Geographic Insight */}
                      {painPointInsights.geographic.topCircle && (
                        <div className={`p-2 rounded text-xs ${
                          config.color === 'red' ? 'bg-gradient-to-r from-red-50 to-orange-50' :
                          config.color === 'orange' ? 'bg-gradient-to-r from-orange-50 to-yellow-50' :
                          'bg-gradient-to-r from-green-50 to-emerald-50'
                        }`}>
                          <span className={`font-medium ${
                            config.color === 'red' ? 'text-red-800' :
                            config.color === 'orange' ? 'text-orange-800' :
                            'text-green-800'
                          }`}>🌍 Geographic:</span>
                          <span className={`ml-1 ${
                            config.color === 'red' ? 'text-red-700' :
                            config.color === 'orange' ? 'text-orange-700' :
                            'text-green-700'
                          }`}>
                            {painPointInsights.geographic.topCircle.circle} {activeTab === 'promoters' ? 'excels' : 'leads'} with {painPointInsights.geographic.topCircle.count} cases
                          </span>
                        </div>
                      )}

                      {/* Interaction Subcategory Insight */}
                      {painPointInsights.interaction.topSubcategory && (
                        <div className="bg-gradient-to-r from-teal-50 to-cyan-50 p-2 rounded text-xs">
                          <span className="font-medium text-teal-800">🔗 Interaction:</span>
                          <span className="text-teal-700 ml-1">
                            {painPointInsights.interaction.topSubcategory.subcategory} channel leads with {painPointInsights.interaction.topSubcategory.count} cases
                            ({Math.round((painPointInsights.interaction.topSubcategory.count / painPointInsights.totalCount) * 100)}%)
                          </span>
                        </div>
                      )}
                    </div>
                    
                    {/* AI/Long-term suggestions - Hide for promoters */}
                    {activeTab !== 'promoters' && (
                      <div className="space-y-2">
                        <div className={`p-2 rounded ${
                          config.color === 'red' ? 'bg-gradient-to-r from-red-50 to-orange-50' :
                          config.color === 'orange' ? 'bg-gradient-to-r from-orange-50 to-yellow-50' :
                          'bg-gradient-to-r from-green-50 to-emerald-50'
                        }`}>
                          <span className={`font-medium text-xs ${
                            config.color === 'red' ? 'text-red-800' :
                            config.color === 'orange' ? 'text-orange-800' :
                            'text-green-800'
                          }`}>🤖 AI Suggestion:</span>
                          <p className={`text-xs mt-0.5 ${
                            config.color === 'red' ? 'text-red-700' :
                            config.color === 'orange' ? 'text-orange-700' :
                            'text-green-700'
                          }`}>
                            {generateSpecificSuggestion(issue.category, issue.severity, issue.count)}
                          </p>
                        </div>
                        <div className={`p-2 rounded ${
                          config.color === 'red' ? 'bg-gradient-to-r from-red-50 to-orange-50' :
                          config.color === 'orange' ? 'bg-gradient-to-r from-orange-50 to-yellow-50' :
                          'bg-gradient-to-r from-green-50 to-emerald-50'
                        }`}>
                          <span className={`font-medium text-xs ${
                            config.color === 'red' ? 'text-red-800' :
                            config.color === 'orange' ? 'text-orange-800' :
                            'text-green-800'
                          }`}>🎯 Long-term:</span>
                          <p className={`text-xs mt-0.5 ${
                            config.color === 'red' ? 'text-red-700' :
                            config.color === 'orange' ? 'text-orange-700' :
                            'text-green-700'
                          }`}>
                            {generateLongTermSolution(issue.category, issue.severity)}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Click for detailed evidence */}
                    <button
                      onClick={() => showEvidence(
                        activeTab === 'promoters' ? `${issue.category} Success Factor Analysis` : `${issue.category} Pain Point Analysis}`,
                        [
                          { 
                            metric: 'Key Insight', 
                            value: `${issue.category} affects ${painPointInsights.totalCount} customers (${issue.percentage}% of ${activeTab === 'promoters' ? 'positive feedback' : 'complaints'}). ${issue.topSubcategories && issue.topSubcategories.length > 0 ? `Primary issues: ${issue.topSubcategories[0].name} (${issue.topSubcategories[0].count} ${activeTab === 'promoters' ? 'positive mentions' : 'complaints'}, ${issue.topSubcategories[0].percentage}% of category)${issue.topSubcategories[1] ? `, ${issue.topSubcategories[1].name} (${issue.topSubcategories[1].count} ${activeTab === 'promoters' ? 'positive mentions' : 'complaints'}, ${issue.topSubcategories[1].percentage}% of category)` : ''}${issue.topSubcategories[2] ? `, ${issue.topSubcategories[2].name} (${issue.topSubcategories[2].count} ${activeTab === 'promoters' ? 'positive mentions' : 'complaints'}, ${issue.topSubcategories[2].percentage}% of category)` : ''}.` : ''} ${painPointInsights.topProduct ? `Top ${activeTab === 'promoters' ? 'performing' : 'affected'} product: ${painPointInsights.topProduct.name}.` : ''}` 
                          },
                          { metric: 'Total Impact', value: `${issue.count} ${activeTab === 'promoters' ? 'positive mentions' : 'complaints'} (${issue.percentage}% of total)` },
                          { metric: 'Severity Level', value: issue.severity },
                          { metric: 'Subcategory Breakdown', value: issue.topSubcategories && issue.topSubcategories.length > 0 ? issue.topSubcategories.map(s => `${s.name}: ${s.count} ${activeTab === 'promoters' ? 'positive mentions' : 'complaints'} (${s.percentage}% of category)`).join(' | ') : 'No specific subcategory data' },
                          ...(painPointInsights.topProduct ? [{ metric: `${activeTab === 'promoters' ? 'Most Performing Product' : 'Most Affected Product'}`, value: `${painPointInsights.topProduct.name} (${painPointInsights.topProduct.count} ${activeTab === 'promoters' ? 'positive mentions' : 'complaints'})` }] : []),
                          ...(painPointInsights.demographics.topAge ? [{ metric: `${activeTab === 'promoters' ? 'Most Engaged Age Group' : 'Most Affected Age Group'}`, value: `${painPointInsights.demographics.topAge.group} (${painPointInsights.demographics.topAge.count} cases)` }] : []),
                          ...(painPointInsights.demographics.topGender ? [{ metric: `${activeTab === 'promoters' ? 'Most Engaged Gender' : 'Most Affected Gender'}`, value: `${painPointInsights.demographics.topGender.gender} (${painPointInsights.demographics.topGender.count} cases)` }] : []),
                          ...(painPointInsights.geographic.topCircle ? [{ metric: `${activeTab === 'promoters' ? 'Top Performing Circle' : 'Top Affected Circle'}`, value: `${painPointInsights.geographic.topCircle.circle} (${painPointInsights.geographic.topCircle.count} cases)` }] : []),
                          ...(painPointInsights.interaction.topSubcategory ? [{ metric: `${activeTab === 'promoters' ? 'Top Performing Interaction Subcategory' : 'Top Affected Interaction Subcategory'}`, value: `${painPointInsights.interaction.topSubcategory.subcategory} (${painPointInsights.interaction.topSubcategory.count} cases)` }] : [])
                        ],
                        `${issue.category} affects ${painPointInsights.totalCount} customers (${issue.percentage}% of ${activeTab === 'promoters' ? 'positive feedback' : 'complaints'}). ${issue.topSubcategories && issue.topSubcategories.length > 0 ? `Primary issues: ${issue.topSubcategories[0].name} (${issue.topSubcategories[0].count} ${activeTab === 'promoters' ? 'positive mentions' : 'complaints'}), ${issue.topSubcategories[1] ? `${issue.topSubcategories[1].name} (${issue.topSubcategories[1].count} ${activeTab === 'promoters' ? 'positive mentions' : 'complaints'})` : ''}. ` : ''} ${painPointInsights.topProduct ? `Top ${activeTab === 'promoters' ? 'performing' : 'affected'} product: ${painPointInsights.topProduct.name}.` : ''} Immediate action: ${generateSpecificSuggestion(issue.category, issue.severity, issue.count)}`
                      )}
                      className="mt-2 flex items-center text-xs text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      <HiCursorArrowRays className="w-3 h-3 mr-1" />
                      <span>View detailed analysis</span>
                    </button>
                  </div>
                );
              })}
            </div>
          </motion.div>

          {/* Demographics Analysis - Separate Row */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            className={`p-4 rounded-lg border mb-4 ${
              config.color === 'red' ? 'bg-gradient-to-r from-red-50 to-orange-50 border-red-200' :
              config.color === 'orange' ? 'bg-gradient-to-r from-orange-50 to-yellow-50 border-orange-200' :
              'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200'
            }`}
          >
            {/* Section Heading */}
            <div className="flex items-center mb-4">
              <HiUserGroup className={`w-5 h-5 mr-3 ${
                config.color === 'red' ? 'text-red-600' :
                config.color === 'orange' ? 'text-orange-600' :
                'text-green-600'
              }`} />
              <h3 className={`text-lg font-bold ${
                config.color === 'red' ? 'text-red-800' :
                config.color === 'orange' ? 'text-orange-800' :
                'text-green-800'
              }`}>Demographics Analysis</h3>
            </div>

            {/* Key Insight at Top */}
            {currentInsights.demographicAnalysis.insights.dominantAge && (
              <div className={`mb-4 p-3 rounded-lg ${
                config.color === 'red' ? 'bg-gradient-to-r from-red-100 to-orange-100' :
                config.color === 'orange' ? 'bg-gradient-to-r from-orange-100 to-yellow-100' :
                'bg-gradient-to-r from-green-100 to-emerald-100'
              }`}>
                <div className="flex items-center mb-2">
                  <IoBulbOutline className="w-4 h-4 text-gray-800 mr-2" />
                  <h4 className="font-medium text-gray-800 text-sm">Key Demographic Insight</h4>
                </div>
                <p className="text-sm text-gray-700">
                  <strong>{activeTab === 'promoters' ? 'Top Contributors:' : 'Most Affected:'}</strong> {currentInsights.demographicAnalysis.insights.dominantAge.group} age group ({currentInsights.demographicAnalysis.insights.dominantAge.percentage}% of {activeTab === 'promoters' ? 'positive feedback' : 'complaints'}) and {currentInsights.demographicAnalysis.insights.dominantGender.gender.toLowerCase()} customers ({currentInsights.demographicAnalysis.insights.dominantGender.percentage}% of {activeTab === 'promoters' ? 'positive feedback' : 'complaints'}) show {activeTab === 'promoters' ? 'highest satisfaction rates' : 'highest dissatisfaction rates'}.
                  {currentInsights.demographicAnalysis.ageGroupInsights[currentInsights.demographicAnalysis.insights.dominantAge.group].topSubcategoryComplaints && currentInsights.demographicAnalysis.ageGroupInsights[currentInsights.demographicAnalysis.insights.dominantAge.group].topSubcategoryComplaints.length > 0 && (
                    <> <strong>{activeTab === 'promoters' ? `Top success areas for ${currentInsights.demographicAnalysis.insights.dominantAge.group}:` : `Top issues for ${currentInsights.demographicAnalysis.insights.dominantAge.group}:`}</strong> {currentInsights.demographicAnalysis.ageGroupInsights[currentInsights.demographicAnalysis.insights.dominantAge.group].mainIssue} ({currentInsights.demographicAnalysis.ageGroupInsights[currentInsights.demographicAnalysis.insights.dominantAge.group].topSubcategoryComplaints.slice(0, 2).map(subcat => `${subcat.complaint.split(' > ')[1] || subcat.complaint}: ${subcat.count}`).join(', ')}).</>
                  )}
                </p>
                {/* Strategic Action - Hide for promoters */}
                {activeTab !== 'promoters' && (
                  <div className={`mt-2 p-2 rounded ${
                    config.color === 'red' ? 'bg-red-50' :
                    config.color === 'orange' ? 'bg-orange-50' :
                    'bg-green-50'
                  }`}>
                    <span className={`font-medium text-xs ${
                      config.color === 'red' ? 'text-red-800' :
                      config.color === 'orange' ? 'text-orange-800' :
                      'text-green-800'
                    }`}>🎯 Strategic Action:</span>
                    <p className={`text-xs mt-1 ${
                      config.color === 'red' ? 'text-red-700' :
                      config.color === 'orange' ? 'text-orange-700' :
                      'text-green-700'
                    }`}>
                      Develop targeted service approaches for {currentInsights.demographicAnalysis.insights.dominantAge.group} customers and {currentInsights.demographicAnalysis.insights.dominantGender.gender.toLowerCase()} demographics. Implement age-appropriate communication channels and gender-specific service delivery methods to reduce dissatisfaction in high-impact segments.
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Age Group Analysis */}
              <div className={`p-4 rounded-lg border ${
                config.color === 'red' ? 'bg-white border-red-200' :
                config.color === 'orange' ? 'bg-white border-orange-200' :
                'bg-white border-green-200'
              }`}>
                <div className="flex items-center mb-3">
                  <IoPeopleOutline className={`w-4 h-4 mr-2 ${
                    config.color === 'red' ? 'text-red-600' :
                    config.color === 'orange' ? 'text-orange-600' :
                    'text-green-600'
                  }`} />
                  <h4 className="font-medium text-gray-800 text-sm">Age Group Distribution</h4>
                </div>
                <div className="space-y-3">
                  {Object.entries(currentInsights.demographicAnalysis.ageGroups).map(([ageGroup, count]) => {
                    const percentage = Math.round((count / currentInsights.demographicAnalysis.totalFeedback) * 100);
                    const isHighest = currentInsights.demographicAnalysis.insights.dominantAge?.group === ageGroup;
                    const groupInsights = currentInsights.demographicAnalysis.ageGroupInsights[ageGroup];
                    return (
                      <div
                        key={ageGroup}
                        className={`p-2 rounded-lg transition-all ${
                          config.color === 'red' ? (
                            isHighest ? 'bg-gradient-to-r from-red-100 to-orange-100 border border-red-300 text-red-800' :
                            'bg-white border border-red-200 text-red-700'
                          ) : config.color === 'orange' ? (
                            isHighest ? 'bg-gradient-to-r from-orange-100 to-yellow-100 border border-orange-300 text-orange-800' :
                            'bg-white border border-orange-200 text-orange-700'
                          ) : (
                            isHighest ? 'bg-gradient-to-r from-green-100 to-emerald-100 border border-green-300 text-green-800' :
                            'bg-white border border-green-200 text-green-700'
                          )
                        }`}
                      >
                        {/* Key Insight Upfront - Hide for promoters */}
                        {isHighest && activeTab !== 'promoters' && (
                          <div className={`mb-2 p-1 rounded text-xs ${
                            config.color === 'red' ? 'bg-gradient-to-r from-red-200 to-orange-200' :
                            config.color === 'orange' ? 'bg-gradient-to-r from-orange-200 to-yellow-200' :
                            'bg-gradient-to-r from-green-200 to-emerald-200'
                          }`}>
                            <div className="flex items-center">
                              <IoBulbOutline className={`w-3 h-3 mr-1 ${
                                config.color === 'red' ? 'text-red-800' :
                                config.color === 'orange' ? 'text-orange-800' :
                                'text-green-800'
                              }`} />
                              <span className="font-medium">This age group represents {percentage}% of customer dissatisfaction, primarily complaining about {groupInsights.mainIssue}
                              {groupInsights.topSubcategoryComplaints && groupInsights.topSubcategoryComplaints.length > 0 && (
                                <> ({groupInsights.topSubcategoryComplaints.slice(0, 2).map(subcat => `${subcat.complaint.split(' > ')[1] || subcat.complaint}: ${subcat.count}`).join(', ')})</>
                              )}.
                              </span>
                            </div>
                          </div>
                        )}
                        
                        <div className="font-medium text-xs">{ageGroup}</div>
                        <div className="text-xs opacity-75">{count} {activeTab === 'promoters' ? 'positive reviews' : 'complaints'}</div>
                        <div className="text-xs font-medium">{groupInsights.mainIssue}</div>
                        <button
                          onClick={() => showEvidence(
                            `${ageGroup} Age Group Analysis`,
                            [
                              { metric: 'Key Insight', value: `This age group represents ${percentage}% of customer ${activeTab === 'promoters' ? 'satisfaction' : 'dissatisfaction'}, primarily ${activeTab === 'promoters' ? 'appreciating' : 'complaining about'} ${groupInsights.mainIssue}. ${isHighest ? `${activeTab === 'promoters' ? 'Shows strong satisfaction patterns worth replicating.' : 'Requires immediate attention due to highest complaint volume.'}` : `${activeTab === 'promoters' ? 'Demonstrates consistent positive engagement.' : 'Monitor for emerging patterns.'}`}` },
                              { metric: `Total ${activeTab === 'promoters' ? 'Positive Reviews' : 'Complaints'}`, value: `${count} ${activeTab === 'promoters' ? 'positive reviews' : 'complaints'}` },
                              { metric: 'Percentage Share', value: `${percentage}% of total ${activeTab === 'promoters' ? 'satisfaction' : 'dissatisfaction'}` },
                              { metric: activeTab === 'promoters' ? 'Main Success Area' : 'Main Issue', value: groupInsights.mainIssue },
                              { metric: activeTab === 'promoters' ? 'Top Success Factors' : 'Top Complaints', value: groupInsights.topComplaints.map(c => `${c.complaint} (${c.count})`).join(', ') },
                              { metric: activeTab === 'promoters' ? 'Top Success Categories' : 'Top Subcategory Issues', value: groupInsights.topSubcategoryComplaints.map(c => `${c.complaint} (${c.count})`).join(', ') || 'No specific subcategory data' }
                            ],
                            activeTab === 'promoters' ? '' : `Develop age-appropriate communication channels and service delivery methods for ${ageGroup} customers. Focus on their specific needs and preferred interaction styles to improve satisfaction rates.`
                          )}
                          className="flex items-center mt-1 text-xs hover:underline"
                        >
                          <HiCursorArrowRays className="w-3 h-3 mr-1 opacity-50" />
                          <span className="opacity-50">View details</span>
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Gender Distribution */}
              <div className={`p-4 rounded-lg border ${
                config.color === 'red' ? 'bg-white border-red-200' :
                config.color === 'orange' ? 'bg-white border-orange-200' :
                'bg-white border-green-200'
              }`}>
                <div className="flex items-center mb-3">
                  <IoPeopleOutline className={`w-4 h-4 mr-2 ${
                    config.color === 'red' ? 'text-red-600' :
                    config.color === 'orange' ? 'text-orange-600' :
                    'text-green-600'
                  }`} />
                  <h4 className="font-medium text-gray-800 text-sm">Gender Distribution</h4>
                </div>
                <div className="space-y-3">
                  {Object.entries(currentInsights.demographicAnalysis.genderDistribution).map(([gender, count]) => {
                    const percentage = Math.round((count / currentInsights.demographicAnalysis.totalFeedback) * 100);
                    const isHighest = currentInsights.demographicAnalysis.insights.dominantGender?.gender === gender;
                    const genderInsights = currentInsights.demographicAnalysis.genderInsights[gender];
                    return (
                      <div
                        key={gender}
                        className={`p-2 rounded-lg transition-all ${
                          config.color === 'red' ? (
                            isHighest ? 'bg-gradient-to-r from-red-100 to-orange-100 border border-red-300 text-red-800' :
                            'bg-white border border-red-200 text-red-700'
                          ) : config.color === 'orange' ? (
                            isHighest ? 'bg-gradient-to-r from-orange-100 to-yellow-100 border border-orange-300 text-orange-800' :
                            'bg-white border border-orange-200 text-orange-700'
                          ) : (
                            isHighest ? 'bg-gradient-to-r from-green-100 to-emerald-100 border border-green-300 text-green-800' :
                            'bg-white border border-green-200 text-green-700'
                          )
                        }`}
                      >
                        {/* Key Insight Upfront - Hide for promoters */}
                        {isHighest && activeTab !== 'promoters' && (
                          <div className={`mb-2 p-1 rounded text-xs ${
                            config.color === 'red' ? 'bg-gradient-to-r from-red-200 to-orange-200' :
                            config.color === 'orange' ? 'bg-gradient-to-r from-orange-200 to-yellow-200' :
                            'bg-gradient-to-r from-green-200 to-emerald-200'
                          }`}>
                            <div className="flex items-center">
                              <IoBulbOutline className={`w-3 h-3 mr-1 ${
                                config.color === 'red' ? 'text-red-800' :
                                config.color === 'orange' ? 'text-orange-800' :
                                'text-green-800'
                              }`} />
                              <span className="font-medium">{gender} customers account for {percentage}% of {activeTab === 'promoters' ? 'positive feedback' : 'dissatisfaction'}, primarily {activeTab === 'promoters' ? 'appreciating' : 'complaining about'} {genderInsights.mainIssue}
                              {genderInsights.topSubcategoryComplaints && genderInsights.topSubcategoryComplaints.length > 0 && (
                                <> ({genderInsights.topSubcategoryComplaints.slice(0, 2).map(subcat => `${subcat.complaint.split(' > ')[1] || subcat.complaint}: ${subcat.count}`).join(', ')})</>
                              )}.
                              </span>
                            </div>
                          </div>
                        )}

                        <div className="font-medium text-xs">{gender}</div>
                        <div className="text-xs opacity-75">{count} {activeTab === 'promoters' ? 'positive reviews' : 'complaints'}</div>
                        <div className="text-xs font-medium">{genderInsights.mainIssue}</div>
                        <button
                          onClick={() => showEvidence(
                            `${gender} Customer Analysis`,
                            [
                              { metric: 'Key Insight', value: `${gender} customers account for ${percentage}% of ${activeTab === 'promoters' ? 'satisfaction' : 'dissatisfaction'}, primarily ${activeTab === 'promoters' ? 'appreciating' : 'complaining about'} ${genderInsights.mainIssue}. ${isHighest ? `${activeTab === 'promoters' ? 'Shows strong satisfaction patterns worth understanding.' : 'Consider gender-specific service improvements.'}` : `${activeTab === 'promoters' ? 'Demonstrates consistent positive engagement.' : 'Relatively lower complaint rate.'}`}` },
                              { metric: `Total ${activeTab === 'promoters' ? 'Positive Reviews' : 'Complaints'}`, value: `${count} ${activeTab === 'promoters' ? 'positive reviews' : 'complaints'}` },
                              { metric: 'Percentage Share', value: `${percentage}% of total ${activeTab === 'promoters' ? 'satisfaction' : 'dissatisfaction'}` },
                              { metric: activeTab === 'promoters' ? 'Main Success Area' : 'Main Issue', value: genderInsights.mainIssue },
                              { metric: activeTab === 'promoters' ? 'Top Success Factors' : 'Top Complaints', value: genderInsights.topComplaints.map(c => `${c.complaint} (${c.count})`).join(', ') },
                              { metric: activeTab === 'promoters' ? 'Top Success Categories' : 'Top Subcategory Issues', value: genderInsights.topSubcategoryComplaints.map(c => `${c.complaint} (${c.count})`).join(', ') || 'No specific subcategory data' }
                            ],
                            activeTab === 'promoters' ? '' : `Implement gender-specific service approaches for ${gender} customers. Focus on communication styles and service preferences that resonate with this demographic to improve satisfaction.`
                          )}
                          className="flex items-center mt-1 text-xs hover:underline"
                        >
                          <HiCursorArrowRays className="w-3 h-3 mr-1 opacity-50" />
                          <span className="opacity-50">View details</span>
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Geographic Analysis - Separate Row */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.25 }}
            className={`p-4 rounded-lg border mb-4 ${
              config.color === 'red' ? 'bg-gradient-to-r from-red-50 to-orange-50 border-red-200' :
              config.color === 'orange' ? 'bg-gradient-to-r from-orange-50 to-yellow-50 border-orange-200' :
              'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200'
            }`}
          >
            {/* Section Heading */}
            <div className="flex items-center mb-4">
              <HiMapPin className={`w-5 h-5 mr-3 ${
                config.color === 'red' ? 'text-red-600' :
                config.color === 'orange' ? 'text-orange-600' :
                'text-green-600'
              }`} />
              <h3 className={`text-lg font-bold ${
                config.color === 'red' ? 'text-red-800' :
                config.color === 'orange' ? 'text-orange-800' :
                'text-green-800'
              }`}>Geographic Analysis</h3>
            </div>

            {/* Key Insight at Top */}
            {currentInsights.geographicAnalysis.topCircles.length > 0 && (
              <div className={`mb-4 p-3 rounded-lg ${
                config.color === 'red' ? 'bg-gradient-to-r from-red-100 to-orange-100' :
                config.color === 'orange' ? 'bg-gradient-to-r from-orange-100 to-yellow-100' :
                'bg-gradient-to-r from-green-100 to-emerald-100'
              }`}>
                <div className="flex items-center mb-2">
                  <IoBulbOutline className="w-4 h-4 text-gray-800 mr-2" />
                  <h4 className="font-medium text-gray-800 text-sm">Key Geographic Insight</h4>
                </div>
                <p className="text-sm text-gray-700">
                  <strong>{activeTab === 'promoters' ? 'Top Performing Region:' : 'Most Affected Region:'}</strong> {currentInsights.geographicAnalysis.topCircles[0].name} {activeTab === 'promoters' ? 'excels with' : 'leads with'} {currentInsights.geographicAnalysis.topCircles[0].count} {activeTab === 'promoters' ? 'positive reviews' : 'complaints'} ({currentInsights.geographicAnalysis.topCircles[0].percentage}% of total). 
                  {currentInsights.geographicAnalysis.topCircles[0].topSubcategoryComplaints && currentInsights.geographicAnalysis.topCircles[0].topSubcategoryComplaints.length > 0 && (
                    <> <strong>{activeTab === 'promoters' ? `Primary success drivers in ${currentInsights.geographicAnalysis.topCircles[0].name}:` : `Primary issues in ${currentInsights.geographicAnalysis.topCircles[0].name}:`}</strong> {currentInsights.geographicAnalysis.topCircles[0].mainIssue} ({currentInsights.geographicAnalysis.topCircles[0].topSubcategoryComplaints.slice(0, 2).map(subcat => `${subcat.complaint.split(' > ')[1] || subcat.complaint}: ${subcat.count}`).join(', ')}).</>
                  )}
                </p>
                {activeTab !== 'promoters' && (
                  <div className={`mt-2 p-2 rounded ${
                    config.color === 'red' ? 'bg-red-50' :
                    config.color === 'orange' ? 'bg-orange-50' :
                    'bg-green-50'
                  }`}>
                    <span className={`font-medium text-xs ${
                      config.color === 'red' ? 'text-red-800' :
                      config.color === 'orange' ? 'text-orange-800' :
                      'text-green-800'
                    }`}>🌍 Regional Strategy:</span>
                    <p className={`text-xs mt-1 ${
                      config.color === 'red' ? 'text-red-700' :
                      config.color === 'orange' ? 'text-orange-700' :
                      'text-green-700'
                    }`}>
                      Deploy specialized support teams and localized service improvements in {currentInsights.geographicAnalysis.topCircles[0].name}. Implement region-specific training programs and infrastructure upgrades to address high-volume complaint areas and improve service delivery consistency.
                    </p>
                  </div>
                )}
              </div>
            )}
            
            {/* Top Circles - 3 tiles in a row */}
            <div className="mb-3">
              <h5 className={`font-medium mb-2 text-xs ${
                config.color === 'red' ? 'text-red-700' :
                config.color === 'orange' ? 'text-orange-700' :
                'text-green-700'
              }`}>{activeTab === 'promoters' ? 'Top Performing Circles' : 'Most Affected Circles'}</h5>
              <div className="grid grid-cols-3 gap-2">
                {currentInsights.geographicAnalysis.topCircles.slice(0, 9).map((circle, idx) => (
                  <div
                    key={circle.name}
                    className={`p-2 rounded-lg transition-all ${
                      config.color === 'red' ? (
                        idx === 0 ? 'bg-gradient-to-r from-red-100 to-orange-100 border border-red-300 text-red-800' :
                        idx === 1
                          ? 'bg-gradient-to-r from-orange-100 to-yellow-100 border border-orange-300 text-orange-800'
                          : 'bg-white border border-red-200 text-red-700'
                      ) : config.color === 'orange' ? (
                        idx === 0 ? 'bg-gradient-to-r from-orange-100 to-yellow-100 border border-orange-300 text-orange-800' :
                        idx === 1 ? 'bg-gradient-to-r from-yellow-100 to-amber-100 border border-yellow-300 text-yellow-800' :
                        'bg-white border border-orange-200 text-orange-700'
                      ) : (
                        idx === 0 ? 'bg-gradient-to-r from-green-100 to-emerald-100 border border-green-300 text-green-800' :
                        idx === 1 ? 'bg-gradient-to-r from-emerald-100 to-teal-100 border border-emerald-300 text-emerald-800' :
                        'bg-white border border-green-200 text-green-700'
                      )
                    }`}
                  >
                    {/* Key Insight Upfront for top circles - Hide for promoters */}
                    {idx < 2 && activeTab !== 'promoters' && (
                      <div className={`mb-2 p-1 rounded text-xs ${
                        config.color === 'red' ? 'bg-gradient-to-r from-red-200 to-orange-200' :
                        config.color === 'orange' ? 'bg-gradient-to-r from-orange-200 to-yellow-200' :
                        'bg-gradient-to-r from-green-200 to-emerald-200'
                      }`}>
                        <div className="flex items-center">
                          <IoBulbOutline className={`w-3 h-3 mr-1 ${
                            config.color === 'red' ? 'text-red-800' :
                            config.color === 'orange' ? 'text-orange-800' :
                            'text-green-800'
                          }`} />
                          <span className="font-medium">{circle.name} accounts for {circle.percentage}% of customer {activeTab === 'promoters' ? 'positive feedback' : 'complaints'}, primarily about {circle.mainIssue}
                          {circle.topSubcategoryComplaints && circle.topSubcategoryComplaints.length > 0 && (
                            <> ({circle.topSubcategoryComplaints.slice(0, 2).map(subcat => `${subcat.complaint.split(' > ')[1] || subcat.complaint}: ${subcat.count}`).join(', ')})</>
                          )}.
                          </span>
                        </div>
                      </div>
                    )}

                    <div className="font-medium text-xs truncate" title={circle.name}>{circle.name}</div>
                    <div className="text-xs opacity-75">{circle.count} {activeTab === 'promoters' ? 'positive reviews' : 'complaints'}</div>
                    <div className="text-xs font-medium truncate" title={circle.mainIssue}>{circle.mainIssue}</div>
                    <div className="text-xs font-bold">{circle.percentage}%</div>
                    <button
                      onClick={() => showEvidence(
                        `${circle.name} Geographic Analysis`,
                        [
                          { metric: 'Key Insight', value: `${circle.name} accounts for ${circle.percentage}% of customer ${activeTab === 'promoters' ? 'positive feedback' : 'complaints'}, primarily about ${circle.mainIssue}. ${circle.percentage > 15 ? `This concentration indicates ${activeTab === 'promoters' ? 'strong regional performance requiring replication across other areas.' : 'systemic regional issues requiring immediate management intervention.'}` : `Monitor for ${activeTab === 'promoters' ? 'successful practices and potential expansion opportunities.' : 'emerging patterns and potential service gaps.'}`}` },
                          { metric: `Total ${activeTab === 'promoters' ? 'Positive Reviews' : 'Complaints'}`, value: `${circle.count} ${activeTab === 'promoters' ? 'positive reviews' : 'complaints'}` },
                          { metric: 'Circle Share', value: `${circle.percentage}% of total ${activeTab === 'promoters' ? 'satisfaction' : 'dissatisfaction'}` },
                          { metric: 'Ranking', value: `#${idx + 1} ${activeTab === 'promoters' ? 'best performing' : 'most affected'} circle` },
                          { metric: activeTab === 'promoters' ? 'Main Success Area' : 'Main Issue', value: circle.mainIssue },
                          { metric: activeTab === 'promoters' ? 'Top Success Factors' : 'Top Complaints', value: circle.topComplaints.map(c => `${c.complaint} (${c.count})`).join(', ') },
                          { metric: activeTab === 'promoters' ? 'Top Success Categories' : 'Top Subcategory Issues', value: circle.topSubcategoryComplaints.map(c => `${c.complaint} (${c.count})`).join(', ') || 'No specific subcategory data' }
                        ],
                        activeTab === 'promoters' ? '' : `Deploy regional intervention team to ${circle.name}. Conduct on-ground assessment, implement localized service improvements, and establish regional quality assurance protocols.`
                      )}
                      className="flex items-center mt-1 text-xs hover:underline"
                    >
                      <HiCursorArrowRays className="w-3 h-3 mr-1 opacity-50" />
                      <span className="opacity-50">Details</span>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Product Analysis Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.3 }}
            className={`p-4 rounded-lg border mb-4 ${
              config.color === 'red' ? 'bg-gradient-to-r from-red-50 to-orange-50 border-red-200' :
              config.color === 'orange' ? 'bg-gradient-to-r from-orange-50 to-yellow-50 border-orange-200' :
              'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200'
            }`}
          >
            {/* Section Heading */}
            <div className="flex items-center mb-4">
              <IoAlert className={`w-5 h-5 mr-3 ${
                config.color === 'red' ? 'text-red-600' :
                config.color === 'orange' ? 'text-orange-600' :
                'text-green-600'
              }`} />
              <h3 className={`text-lg font-bold ${
                config.color === 'red' ? 'text-red-800' :
                config.color === 'orange' ? 'text-orange-800' :
                'text-green-800'
              }`}>Product Performance Analysis</h3>
            </div>

            {/* Key Insight at Top */}
            {currentInsights.productAnalysis.mostProblematicProduct && (
              <div className={`mb-4 p-3 rounded-lg ${
                config.color === 'red' ? 'bg-gradient-to-r from-red-100 to-orange-100' :
                config.color === 'orange' ? 'bg-gradient-to-r from-orange-100 to-yellow-100' :
                'bg-gradient-to-r from-green-100 to-emerald-100'
              }`}>
                <div className="flex items-center mb-2">
                  <IoBulbOutline className="w-4 h-4 text-gray-800 mr-2" />
                  <h4 className="font-medium text-gray-800 text-sm">Key Product Insight</h4>
                </div>
                <p className="text-sm text-gray-700">
                  <strong>{activeTab === 'promoters' ? 'Top Performing Product:' : 'Most Problematic Product:'}</strong> {currentInsights.productAnalysis.mostProblematicProduct.name} accounts for {currentInsights.productAnalysis.mostProblematicProduct.percentage}% of all {activeTab === 'promoters' ? 'positive feedback' : 'complaints'} ({currentInsights.productAnalysis.mostProblematicProduct.count} {activeTab === 'promoters' ? 'positive reviews' : 'complaints'}).
                  {currentInsights.productAnalysis.mostProblematicProduct.topSubcategoryComplaints && currentInsights.productAnalysis.mostProblematicProduct.topSubcategoryComplaints.length > 0 && (
                    <> <strong>{activeTab === 'promoters' ? 'Main success areas:' : 'Main issues:'}</strong> {currentInsights.productAnalysis.mostProblematicProduct.mainIssue} ({currentInsights.productAnalysis.mostProblematicProduct.topSubcategoryComplaints.slice(0, 2).map(subcat => `${subcat.complaint.split(' > ')[1] || subcat.complaint}: ${subcat.count}`).join(', ')}).</>
                  )} {activeTab === 'promoters' ? '' : 'This requires immediate product team intervention and quality review.'}
                </p>
                {activeTab !== 'promoters' && (
                  <div className={`mt-2 p-2 rounded ${
                    config.color === 'red' ? 'bg-red-50' :
                    config.color === 'orange' ? 'bg-orange-50' :
                    'bg-green-50'
                  }`}>
                    <span className={`font-medium text-xs ${
                      config.color === 'red' ? 'text-red-800' :
                      config.color === 'orange' ? 'text-orange-800' :
                      'text-green-800'
                    }`}>📦 Product Strategy:</span>
                    <p className={`text-xs mt-1 ${
                      config.color === 'red' ? 'text-red-700' :
                      config.color === 'orange' ? 'text-orange-700' :
                      'text-green-700'
                    }`}>
                      Focus immediate resources on {currentInsights.productAnalysis.mostProblematicProduct.name} quality control and customer callback program. Implement batch inspection protocols and establish dedicated customer support team for ${activeTab === 'promoters' ? 'engaged users' : 'affected users'}.
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {currentInsights.productAnalysis.topProducts.slice(0, 6).map((product, idx) => (
                <div
                  key={product.name}
                  className={`p-3 rounded-lg border transition-all ${
                    config.color === 'red' ? (
                      idx === 0 ? 'bg-gradient-to-r from-red-100 to-orange-100 border-red-300' :
                      idx < 3 ? 'bg-gradient-to-r from-orange-100 to-yellow-100 border-orange-300' :
                      'bg-white border-red-200'
                    ) : config.color === 'orange' ? (
                      idx === 0 ? 'bg-gradient-to-r from-orange-100 to-yellow-100 border-orange-300' :
                      idx < 3 ? 'bg-gradient-to-r from-yellow-100 to-amber-100 border-yellow-300' :
                      'bg-white border-orange-200'
                    ) : (
                      idx === 0 ? 'bg-gradient-to-r from-green-100 to-emerald-100 border-green-300' :
                      idx < 3 ? 'bg-gradient-to-r from-emerald-100 to-teal-100 border-emerald-300' :
                      'bg-white border-green-200'
                    )
                  }`}
                >
                  {/* Key Insight Upfront for top products - Hide for promoters */}
                  {idx === 0 && activeTab !== 'promoters' && (
                    <div className={`mb-2 p-2 rounded text-xs ${
                      config.color === 'red' ? 'bg-gradient-to-r from-red-200 to-orange-200' :
                      config.color === 'orange' ? 'bg-gradient-to-r from-orange-200 to-yellow-200' :
                      'bg-gradient-to-r from-green-200 to-emerald-200'
                    }`}>
                      <div className="flex items-center">
                        <IoBulbOutline className={`w-3 h-3 mr-1 ${
                          config.color === 'red' ? 'text-red-800' :
                          config.color === 'orange' ? 'text-orange-800' :
                          'text-green-800'
                        }`} />
                        <span className="font-medium">{product.name} accounts for {product.percentage}% of all {activeTab === 'promoters' ? 'positive feedback' : 'complaints'} ({product.count} {activeTab === 'promoters' ? 'positive reviews' : 'complaints'}). Main issues: {product.mainIssue}
                        {product.topSubcategoryComplaints && product.topSubcategoryComplaints.length > 0 && (
                          <> ({product.topSubcategoryComplaints.slice(0, 2).map(subcat => `${subcat.complaint.split(' > ')[1] || subcat.complaint}: ${subcat.count}`).join(', ')})</>
                        )}. {activeTab === 'promoters' ? 'This shows excellent product performance.' : 'This requires immediate product team intervention and quality review.'}
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="font-medium text-xs mb-1">{product.name}</div>
                  <div className="text-xs opacity-75 mb-2">{product.count} {activeTab === 'promoters' ? 'positive reviews' : 'complaints'} ({product.percentage}%)</div>
                  <div className="text-xs font-medium text-gray-700">{activeTab === 'promoters' ? 'Main Success Area' : 'Main Issue'}: {product.mainIssue}</div>
                  {idx === 0 && activeTab !== 'promoters' && (
                    <div className={`mt-2 px-2 py-1 rounded text-xs ${
                      config.color === 'red' ? 'bg-gradient-to-r from-red-200 to-orange-200 text-red-800' :
                      config.color === 'orange' ? 'bg-gradient-to-r from-orange-200 to-yellow-200 text-orange-800' :
                      'bg-gradient-to-r from-green-200 to-emerald-200 text-green-800'
                    }`}>
                      🚨 Requires Immediate Attention
                    </div>
                  )}
                  <button
                    onClick={() => showEvidence(
                      `${product.name} Product Analysis`,
                      [
                        { metric: 'Key Insight', value: `${product.name} accounts for ${product.percentage}% of all ${activeTab === 'promoters' ? 'positive feedback' : 'complaints'} (${product.count} ${activeTab === 'promoters' ? 'positive reviews' : 'complaints'}). ${idx === 0 && activeTab !== 'promoters' ? 'This requires immediate product team intervention and quality review.' : activeTab === 'promoters' ? 'This demonstrates strong product performance.' : 'Monitor for trends and patterns.'}` },
                        { metric: `Total ${activeTab === 'promoters' ? 'Positive Reviews' : 'Complaints'}`, value: `${product.count} ${activeTab === 'promoters' ? 'positive reviews' : 'complaints'}` },
                        { metric: 'Product Share', value: `${product.percentage}% of total ${activeTab === 'promoters' ? 'satisfaction' : 'dissatisfaction'}` },
                        { metric: 'Product Ranking', value: `#${idx + 1} ${activeTab === 'promoters' ? 'best performing' : 'most problematic'} product` },
                        { metric: activeTab === 'promoters' ? 'Main Success Area' : 'Main Issue', value: product.mainIssue },
                        { metric: activeTab === 'promoters' ? 'Top Success Factors' : 'Top Complaints', value: product.topComplaints.map(c => `${c.complaint} (${c.count})`).join(', ') },
                        { metric: activeTab === 'promoters' ? 'Top Success Categories' : 'Top Subcategory Issues', value: product.topSubcategoryComplaints.map(c => `${c.complaint} (${c.count})`).join(', ') || 'No specific subcategory data' }
                      ],
                      activeTab === 'promoters' ? '' : `Focus immediate resources on ${product.name} quality control and customer callback program. Implement batch inspection protocols and establish dedicated customer support team for ${activeTab === 'promoters' ? 'engaged users' : 'affected users'}.`
                    )}
                    className="flex items-center mt-2 text-xs hover:underline"
                  >
                    <HiCursorArrowRays className="w-3 h-3 mr-1 opacity-50" />
                    <span className="opacity-50">View details</span>
                  </button>
                </div>
              )) || (
                <div className="col-span-3 text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-600">No specific product data available for analysis</p>
                </div>
              )}
            </div>
          </motion.div>

          {/* Interaction Subcategory Section - Moved above Case Classification */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.35 }}
            className={`p-4 rounded-lg border mb-4 ${
              config.color === 'red' ? 'bg-gradient-to-r from-red-50 to-orange-50 border-red-200' :
              config.color === 'orange' ? 'bg-gradient-to-r from-orange-50 to-yellow-50 border-orange-200' :
              'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200'
            }`}
          >
            {/* Section Heading */}
            <div className="flex items-center mb-4">
              <IoPeopleOutline className={`w-5 h-5 mr-3 ${
                config.color === 'red' ? 'text-red-600' :
                config.color === 'orange' ? 'text-orange-600' :
                'text-green-600'
              }`} />
              <h3 className={`text-lg font-bold ${
                config.color === 'red' ? 'text-red-800' :
                config.color === 'orange' ? 'text-orange-800' :
                'text-green-800'
              }`}>Interaction Subcategory Analysis</h3>
            </div>

            {/* Key Insight at Top */}
            {currentInsights.interactionSubcategory.mostProblematicSubcategory && (
              <div className={`mb-4 p-3 rounded-lg ${
                config.color === 'red' ? 'bg-gradient-to-r from-red-100 to-orange-100' :
                config.color === 'orange' ? 'bg-gradient-to-r from-orange-100 to-yellow-100' :
                'bg-gradient-to-r from-green-100 to-emerald-100'
              }`}>
                <div className="flex items-center mb-2">
                  <IoBulbOutline className="w-4 h-4 text-gray-800 mr-2" />
                  <h4 className="font-medium text-gray-800 text-sm">Key Interaction Insight</h4>
                </div>
                <p className="text-sm text-gray-700">
                  <strong>{activeTab === 'promoters' ? 'Top Performing Interaction:' : 'Most Problematic Interaction Subcategory:'}</strong> {currentInsights.interactionSubcategory.mostProblematicSubcategory.name} generates {currentInsights.interactionSubcategory.mostProblematicSubcategory.percentage}% of {activeTab === 'promoters' ? 'positive feedback' : 'complaints'} ({currentInsights.interactionSubcategory.mostProblematicSubcategory.count} {activeTab === 'promoters' ? 'positive reviews' : 'complaints'}).
                  {currentInsights.interactionSubcategory.topInteractionSubcategories.length > 0 && (
                    <> <strong>{activeTab === 'promoters' ? 'Main success areas:' : 'Main issues:'}</strong> {currentInsights.interactionSubcategory.topInteractionSubcategories.slice(0, 2).map(subcategory => `${subcategory.name} (${subcategory.count} ${activeTab === 'promoters' ? 'positive reviews' : 'complaints'}, ${subcategory.percentage}% of total)`).join(', ')}</>
                  )} {activeTab === 'promoters' ? '' : 'This requires immediate interaction subcategory-specific intervention.'}
                </p>
                {activeTab !== 'promoters' && (
                  <div className={`mt-2 p-2 rounded ${
                    config.color === 'red' ? 'bg-red-50' :
                    config.color === 'orange' ? 'bg-orange-50' :
                    'bg-green-50'
                  }`}>
                    <span className={`font-medium text-xs ${
                      config.color === 'red' ? 'text-red-800' :
                      config.color === 'orange' ? 'text-orange-800' :
                      'text-green-800'
                    }`}>💬 Interaction Strategy:</span>
                    <p className={`text-xs mt-1 ${
                      config.color === 'red' ? 'text-red-700' :
                      config.color === 'orange' ? 'text-orange-700' :
                      'text-green-700'
                    }`}>
                      Deploy specialized training for {currentInsights.interactionSubcategory.mostProblematicSubcategory.name} interactions. Implement enhanced communication protocols and establish dedicated escalation procedures for this interaction type.
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {currentInsights.interactionSubcategory.topInteractionSubcategories.slice(0, 6).map((subcategory, idx) => (
                <div
                  key={subcategory.name}
                  className={`p-3 rounded-lg border transition-all ${
                    config.color === 'red' ? (
                      idx === 0 ? 'bg-gradient-to-r from-red-100 to-orange-100 border-red-300' :
                      idx < 3 ? 'bg-gradient-to-r from-orange-100 to-yellow-100 border-orange-300' :
                      'bg-white border-red-200'
                    ) : config.color === 'orange' ? (
                      idx === 0 ? 'bg-gradient-to-r from-orange-100 to-yellow-100 border-orange-300' :
                      idx < 3 ? 'bg-gradient-to-r from-yellow-100 to-amber-100 border-yellow-300' :
                      'bg-white border-orange-200'
                    ) : (
                      idx === 0 ? 'bg-gradient-to-r from-green-100 to-emerald-100 border-green-300' :
                      idx < 3 ? 'bg-gradient-to-r from-emerald-100 to-teal-100 border-emerald-300' :
                      'bg-white border-green-200'
                    )
                  }`}
                >
                  {/* Key Insight Upfront for top interaction subcategories - Hide for promoters */}
                  {idx === 0 && activeTab !== 'promoters' && (
                    <div className={`mb-2 p-2 rounded text-xs ${
                      config.color === 'red' ? 'bg-gradient-to-r from-red-200 to-orange-200' :
                      config.color === 'orange' ? 'bg-gradient-to-r from-orange-200 to-yellow-200' :
                      'bg-gradient-to-r from-green-200 to-emerald-200'
                    }`}>
                      <div className="flex items-center">
                        <IoBulbOutline className={`w-3 h-3 mr-1 ${
                          config.color === 'red' ? 'text-red-800' :
                          config.color === 'orange' ? 'text-orange-800' :
                          'text-green-800'
                        }`} />
                        <span className="font-medium">{subcategory.name} interaction subcategory generates {subcategory.percentage}% of {activeTab === 'promoters' ? 'positive feedback' : 'complaints'} ({subcategory.count} {activeTab === 'promoters' ? 'positive reviews' : 'complaints'}). Main issue: {subcategory.mainIssue}
                        {subcategory.topSubcategoryComplaints && subcategory.topSubcategoryComplaints.length > 0 && (
                          <> ({subcategory.topSubcategoryComplaints.slice(0, 2).map(subcat => `${subcat.complaint.split(' > ')[1] || subcat.complaint}: ${subcat.count}`).join(', ')})</>
                        )}. {activeTab === 'promoters' ? 'This shows excellent interaction performance.' : 'This requires immediate interaction subcategory-specific intervention.'}
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="font-medium text-xs mb-1">{subcategory.name}</div>
                  <div className="text-xs opacity-75 mb-2">{subcategory.count} {activeTab === 'promoters' ? 'positive reviews' : 'complaints'} ({subcategory.percentage}%)</div>
                  <div className="text-xs font-medium text-gray-700">{activeTab === 'promoters' ? 'Main Success Area' : 'Main Issue'}: {subcategory.mainIssue}</div>
                  {idx === 0 && activeTab !== 'promoters' && (
                    <div className={`mt-2 px-2 py-1 rounded text-xs ${
                      config.color === 'red' ? 'bg-gradient-to-r from-red-200 to-orange-200 text-red-800' :
                      config.color === 'orange' ? 'bg-gradient-to-r from-orange-200 to-yellow-200 text-orange-800' :
                      'bg-gradient-to-r from-green-200 to-emerald-200 text-green-800'
                    }`}>
                      🚨 Requires Immediate Attention
                    </div>
                  )}
                  <button
                    onClick={() => showEvidence(
                      `${subcategory.name} Interaction Subcategory Analysis`,
                      [
                        { metric: 'Key Insight', value: `${subcategory.name} interaction subcategory generates ${subcategory.percentage}% of ${activeTab === 'promoters' ? 'positive feedback' : 'complaints'} (${subcategory.count} ${activeTab === 'promoters' ? 'positive reviews' : 'complaints'}). ${idx === 0 && activeTab !== 'promoters' ? 'This requires immediate interaction subcategory-specific intervention.' : activeTab === 'promoters' ? 'This demonstrates strong interaction performance.' : 'Monitor for trends and patterns.'}` },
                        { metric: `Total ${activeTab === 'promoters' ? 'Positive Reviews' : 'Complaints'}`, value: `${subcategory.count} ${activeTab === 'promoters' ? 'positive reviews' : 'complaints'}` },
                        { metric: 'Subcategory Share', value: `${subcategory.percentage}% of total ${activeTab === 'promoters' ? 'satisfaction' : 'dissatisfaction'}` },
                        { metric: 'Ranking', value: `#${idx + 1} ${activeTab === 'promoters' ? 'best performing' : 'most problematic'} interaction subcategory` },
                        { metric: activeTab === 'promoters' ? 'Main Success Area' : 'Main Issue', value: subcategory.mainIssue },
                        { metric: activeTab === 'promoters' ? 'Top Success Factors' : 'Top Complaints', value: subcategory.topComplaints.map(c => `${c.complaint} (${c.count})`).join(', ') },
                        { metric: activeTab === 'promoters' ? 'Top Success Categories' : 'Top Subcategory Issues', value: subcategory.topSubcategoryComplaints.map(c => `${c.complaint} (${c.count})`).join(', ') || 'No specific subcategory data' }
                      ],
                      activeTab === 'promoters' ? '' : `Deploy specialized training for ${subcategory.name} interactions. Implement enhanced communication protocols and establish dedicated escalation procedures for this interaction type.`
                    )}
                    className="flex items-center mt-2 text-xs hover:underline"
                  >
                    <HiCursorArrowRays className="w-3 h-3 mr-1 opacity-50" />
                    <span className="opacity-50">View details</span>
                  </button>
                </div>
              )) || (
                <div className="col-span-3 text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-600">No specific interaction subcategory data available for analysis</p>
                </div>
              )}
            </div>
          </motion.div>

          {/* Case Classification Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.4 }}
            className={`p-4 rounded-lg border mb-4 ${
              config.color === 'red' ? 'bg-gradient-to-r from-red-50 to-orange-50 border-red-200' :
              config.color === 'orange' ? 'bg-gradient-to-r from-orange-50 to-yellow-50 border-orange-200' :
              'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200'
            }`}
          >
            {/* Section Heading */}
            <div className="flex items-center mb-4">
              <IoInformationCircleOutline className={`w-5 h-5 mr-3 ${
                config.color === 'red' ? 'text-red-600' :
                config.color === 'orange' ? 'text-orange-600' :
                'text-green-600'
              }`} />
              <h3 className={`text-lg font-bold ${
                config.color === 'red' ? 'text-red-800' :
                config.color === 'orange' ? 'text-orange-800' :
                'text-green-800'
              }`}>Case Classification Analysis</h3>
            </div>

            {/* Key Insight at Top */}
            {currentInsights.caseClassification.mostProblematicClassification && (
              <div className={`mb-4 p-3 rounded-lg ${
                config.color === 'red' ? 'bg-gradient-to-r from-red-100 to-orange-100' :
                config.color === 'orange' ? 'bg-gradient-to-r from-orange-100 to-yellow-100' :
                'bg-gradient-to-r from-green-100 to-emerald-100'
              }`}>
                <div className="flex items-center mb-2">
                  <IoBulbOutline className={`w-4 h-4 mr-2 ${
                    config.color === 'red' ? 'text-red-600' :
                    config.color === 'orange' ? 'text-orange-600' :
                    'text-green-600'
                  }`} />
                  <h4 className={`font-medium text-sm ${
                    config.color === 'red' ? 'text-red-800' :
                    config.color === 'orange' ? 'text-orange-800' :
                    'text-green-800'
                  }`}>Key Case Classification Insight</h4>
                </div>
                <p className={`text-xs ${
                  config.color === 'red' ? 'text-red-800' :
                  config.color === 'orange' ? 'text-orange-800' :
                  'text-green-800'
                }`}>
                  <strong>{activeTab === 'promoters' ? 'Top Performing Classification:' : 'Most Problematic Classification:'}</strong> {currentInsights.caseClassification.mostProblematicClassification.name} accounts for {currentInsights.caseClassification.mostProblematicClassification.percentage}% of all {activeTab === 'promoters' ? 'positive feedback' : 'complaints'} ({currentInsights.caseClassification.mostProblematicClassification.count} {activeTab === 'promoters' ? 'positive reviews' : 'complaints'}).
                  {currentInsights.caseClassification.topClassifications.length > 0 && (
                    <> <strong>{activeTab === 'promoters' ? 'Main success areas:' : 'Main issues:'}</strong> {currentInsights.caseClassification.topClassifications.slice(0, 2).map(classification => `${classification.name} (${classification.count} ${activeTab === 'promoters' ? 'positive reviews' : 'complaints'}, ${classification.percentage}% of total)`).join(', ')}</>
                  )} {activeTab === 'promoters' ? '' : 'This requires immediate classification-specific intervention.'}
                </p>
                {activeTab !== 'promoters' && (
                  <div className={`mt-2 p-2 rounded text-xs ${
                    config.color === 'red' ? 'bg-red-50' :
                    config.color === 'orange' ? 'bg-orange-50' :
                    'bg-green-50'
                  }`}>
                    <span className={`font-medium ${
                      config.color === 'red' ? 'text-red-700' :
                      config.color === 'orange' ? 'text-orange-700' :
                      'text-green-700'
                    }`}>🎯 Strategic Insight:</span>
                    <span className={`ml-1 ${
                      config.color === 'red' ? 'text-red-600' :
                      config.color === 'orange' ? 'text-orange-600' :
                      'text-green-600'
                    }`}>
                      Implement specialized case handling protocols for {currentInsights.caseClassification.mostProblematicClassification.name} classifications. Establish dedicated resolution teams and develop classification-specific SOP improvements.
                    </span>
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {currentInsights.caseClassification.topClassifications.slice(0, 6).map((classification, idx) => (
                <div
                  key={classification.name}
                  className={`p-3 rounded-lg border transition-all ${
                    config.color === 'red' ? (
                      idx === 0 ? 'bg-gradient-to-r from-red-100 to-orange-100 border-red-300' :
                      idx < 3 ? 'bg-gradient-to-r from-orange-100 to-yellow-100 border-orange-300' :
                      'bg-white border-red-200'
                    ) : config.color === 'orange' ? (
                      idx === 0 ? 'bg-gradient-to-r from-orange-100 to-yellow-100 border-orange-300' :
                      idx < 3 ? 'bg-gradient-to-r from-yellow-100 to-amber-100 border-yellow-300' :
                      'bg-white border-orange-200'
                    ) : (
                      idx === 0 ? 'bg-gradient-to-r from-green-100 to-emerald-100 border-green-300' :
                      idx < 3 ? 'bg-gradient-to-r from-emerald-100 to-teal-100 border-emerald-300' :
                      'bg-white border-green-200'
                    )
                  }`}
                >
                  {/* Key Insight Upfront for top classifications - Hide for promoters */}
                  {idx === 0 && activeTab !== 'promoters' && (
                    <div className={`mb-2 p-2 rounded text-xs ${
                      config.color === 'red' ? 'bg-gradient-to-r from-red-200 to-orange-200' :
                      config.color === 'orange' ? 'bg-gradient-to-r from-orange-200 to-yellow-200' :
                      'bg-gradient-to-r from-green-200 to-emerald-200'
                    }`}>
                      <div className="flex items-center">
                        <IoBulbOutline className={`w-3 h-3 mr-1 ${
                          config.color === 'red' ? 'text-red-800' :
                          config.color === 'orange' ? 'text-orange-800' :
                          'text-green-800'
                        }`} />
                        <span className="font-medium">{classification.name} accounts for {classification.percentage}% of all complaints ({classification.count} complaints). Main issue: {classification.mainIssue}
                        {classification.topSubcategoryComplaints && classification.topSubcategoryComplaints.length > 0 && (
                          <> ({classification.topSubcategoryComplaints.slice(0, 2).map(subcat => `${subcat.complaint.split(' > ')[1] || subcat.complaint}: ${subcat.count}`).join(', ')})</>
                        )}. {activeTab === 'promoters' ? 'This shows excellent case handling performance.' : 'This requires immediate classification-specific intervention.'}
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="font-medium text-xs mb-1">{classification.name}</div>
                  <div className="text-xs opacity-75 mb-2">{classification.count} {activeTab === 'promoters' ? 'positive reviews' : 'complaints'} ({classification.percentage}%)</div>
                  <div className="text-xs font-medium text-gray-700">{activeTab === 'promoters' ? 'Main Success Area' : 'Main Issue'}: {classification.mainIssue}</div>
                  {idx === 0 && activeTab !== 'promoters' && (
                    <div className={`mt-2 px-2 py-1 rounded text-xs ${
                      config.color === 'red' ? 'bg-gradient-to-r from-red-200 to-orange-200 text-red-800' :
                      config.color === 'orange' ? 'bg-gradient-to-r from-orange-200 to-yellow-200 text-orange-800' :
                      'bg-gradient-to-r from-green-200 to-emerald-200 text-green-800'
                    }`}>
                      🚨 Requires Immediate Attention
                    </div>
                  )}
                  <button
                    onClick={() => showEvidence(
                      `${classification.name} Case Classification Analysis`,
                      [
                        { metric: 'Key Insight', value: `${classification.name} accounts for ${classification.percentage}% of all ${activeTab === 'promoters' ? 'positive feedback' : 'complaints'} (${classification.count} ${activeTab === 'promoters' ? 'positive reviews' : 'complaints'}). ${idx === 0 && activeTab !== 'promoters' ? 'This requires immediate classification-specific intervention.' : activeTab === 'promoters' ? 'This demonstrates strong case handling performance.' : 'Monitor for trends and patterns.'}` },
                        { metric: `Total ${activeTab === 'promoters' ? 'Positive Reviews' : 'Complaints'}`, value: `${classification.count} ${activeTab === 'promoters' ? 'positive reviews' : 'complaints'}` },
                        { metric: 'Classification Share', value: `${classification.percentage}% of total ${activeTab === 'promoters' ? 'satisfaction' : 'dissatisfaction'}` },
                        { metric: 'Ranking', value: `#${idx + 1} ${activeTab === 'promoters' ? 'best performing' : 'most problematic'} case classification` },
                        { metric: activeTab === 'promoters' ? 'Main Success Area' : 'Main Issue', value: classification.mainIssue },
                        { metric: activeTab === 'promoters' ? 'Top Success Factors' : 'Top Complaints', value: classification.topComplaints.map(c => `${c.complaint} (${c.count})`).join(', ') },
                        { metric: activeTab === 'promoters' ? 'Top Success Categories' : 'Top Subcategory Issues', value: classification.topSubcategoryComplaints.map(c => `${c.complaint} (${c.count})`).join(', ') || 'No specific subcategory data' }
                      ],
                      activeTab === 'promoters' ? '' : `Implement specialized case handling protocols for ${classification.name} classifications. Establish dedicated resolution teams and develop classification-specific SOP improvements.`
                    )}
                    className="flex items-center mt-2 text-xs hover:underline"
                  >
                    <HiCursorArrowRays className="w-3 h-3 mr-1 opacity-50" />
                    <span className="opacity-50">View details</span>
                  </button>
                </div>
              )) || (
                <div className="col-span-3 text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-600">No specific case classification data available for analysis</p>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </div>
  );
};

export default TopPainPoints; 